import { useState, useEffect } from "react";


// ── API ───────────────────────────────────────────────────────────────────────
const API = window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api';

async function apiFetch(path, options={}) {
  const token = localStorage.getItem('token');
  const res = await fetch(API + path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}), ...options.headers }
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Error del servidor'); }
  return res.json();
}

// ── Festivos Colombia 2026 ────────────────────────────────────────────────────
const FESTIVOS_2026 = new Set([
  "2026-01-01","2026-01-12","2026-03-23","2026-04-02","2026-04-03",
  "2026-05-01","2026-05-18","2026-06-08","2026-06-29","2026-07-13","2026-07-20",
  "2026-08-07","2026-08-17","2026-10-12","2026-11-02","2026-11-16",
  "2026-12-08","2026-12-25",
]);
const esFestivo = (d) => FESTIVOS_2026.has(d);
const esDomingo = (d) => new Date(d+"T12:00:00").getDay() === 0;
const esSabado = (d) => new Date(d+"T12:00:00").getDay() === 6;

// ── Helpers ───────────────────────────────────────────────────────────────────
const initials = (n) => n.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
const fmt = (n) => new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(n);
const fmtDate = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("es-CO",{day:"2-digit",month:"short",year:"numeric"}) : "—";
const fmtDateShort = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("es-CO",{weekday:"long",day:"2-digit",month:"long"}) : "—";
const toMins = (t) => { if (!t) return 0; const [h,m] = t.split(":").map(Number); return h*60+m; };
const fromMins = (m) => { const h=Math.floor(Math.abs(m)/60); const min=Math.abs(m)%60; return `${h}h ${String(min).padStart(2,"0")}m`; };
const fromMinsHHMM = (m) => { const h=Math.floor(Math.abs(m)/60); const min=Math.abs(m)%60; return `${h}:${String(min).padStart(2,"0")}`; };
const cap = (s) => s ? s.charAt(0).toUpperCase()+s.slice(1) : s;

// Compara la fecha del día trabajado contra la fecha/hora real de envío del registro
const infoEnvio = (fecha, createdAt) => {
  if (!createdAt) return null;
  const dEnvio = new Date(createdAt);
  const soloFecha = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDias = Math.round((soloFecha(dEnvio) - soloFecha(new Date(fecha+"T00:00:00"))) / 86400000);
  const horaStr = dEnvio.toLocaleString("es-CO",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"});
  if (diffDias<=0) return { label:`Enviado el mismo día · ${horaStr}`, color:"#2E7D52" };
  if (diffDias===1) return { label:`Enviado al día siguiente · ${horaStr}`, color:"#B77400" };
  return { label:`Enviado ${diffDias} días después · ${horaStr}`, color:"#D32F2F" };
};

// ── Motor de liquidación ──────────────────────────────────────────────────────
// Reglas Angela Duran:
// - Jornada L-V: 7h30m (450 mins) ordinarios por día
// - Jornada Sábado: 4h30m (270 mins) ordinarios por día
// - Divisor: (42÷6)×30 = 210h/mes → valor hora = salario÷210
// - Diurno: 6:00 AM – 7:00 PM (360–1140 mins)
// - Nocturno: 7:00 PM – 6:00 AM
// - Extra diurna: ×1.25 | Extra nocturna: ×1.75 | Recargo noct. ordinario: +35%
// - Horas extra se calculan DIARIAMENTE (no semanalmente)
// - Bloques pares = trabajo, bloques impares = descanso

function calcularMinutosDia(bloques, fecha) {
  // Retorna minutos por tipo:
  // ordinarioDiurno, ordinarioNocturno (+35%)
  // extraDiurna (+25%), extraNocturna (+75%)
  // extraFestDiurna (+100%), extraFestNocturna (+150%)
  //
  // LÓGICA: todo el tiempo entre la PRIMERA entrada y la ÚLTIMA salida cuenta
  // como trabajo, EXCEPTO los minutos explícitamente marcados como descanso
  // (desayuno), almuerzo o permiso. Huecos sin registrar entre bloques de
  // trabajo se consideran trabajo automáticamente.

  const sab = esSabado(fecha);
  const fest = esFestivo(fecha) || esDomingo(fecha);
  const jornadaOrdinaria = sab ? 270 : 450; // 4h30m sáb, 7h30m L-V
  const DIURNO_INI = 360;  // 6:00 AM
  const DIURNO_FIN = 1140; // 7:00 PM

  // Bloques con hora válida (trabajo o pausa con entrada/salida)
  const todosBloques = bloques.filter(b => b.entrada && b.salida);
  if (todosBloques.length === 0) {
    return { ordinarioDiurno:0, ordinarioNocturno:0, extraDiurna:0, extraNocturna:0, extraFestDiurna:0, extraFestNocturna:0, totalTrabajado:0 };
  }

  // Normalizar a minutos absolutos (manejando cruce de medianoche relativo al primer bloque)
  const norm = todosBloques.map(b => {
    let ini = toMins(b.entrada);
    let fin = toMins(b.salida);
    if (fin <= ini) fin += 1440; // cruza medianoche
    return { tipo: b.tipo, ini, fin };
  }).sort((a,b)=>a.ini-b.ini);

  const inicioReal = norm[0].ini;
  const finReal = Math.max(...norm.map(b=>b.fin));

  // Construir set de minutos marcados como PAUSA (descanso/almuerzo/permiso)
  // Cualquier minuto en [inicioReal, finReal) que no esté en una pausa = trabajo
  const pausas = norm.filter(b => b.tipo !== "trabajo");

  const enPausa = (min) => pausas.some(p => min >= p.ini && min < p.fin);

  // Recorrer minuto a minuto el rango completo, clasificando como trabajo
  // (todo lo que no es pausa) según jornada ordinaria/extra y diurno/nocturno
  let minutosAcumulados = 0;
  let ordinarioDiurno = 0, ordinarioNocturno = 0;
  let extraDiurna = 0, extraNocturna = 0;
  let extraFestDiurna = 0, extraFestNocturna = 0;

  let cursor = inicioReal;
  while (cursor < finReal) {
    if (enPausa(cursor)) { cursor++; continue; }

    const ordinarioRestante = Math.max(0, jornadaOrdinaria - minutosAcumulados);
    const esOrdinario = ordinarioRestante > 0 && !fest;
    const cursorMod = cursor % 1440;
    const esDiurno = cursorMod >= DIURNO_INI && cursorMod < DIURNO_FIN;

    if (fest) {
      if (esDiurno) extraFestDiurna++; else extraFestNocturna++;
    } else if (esOrdinario) {
      if (esDiurno) ordinarioDiurno++; else ordinarioNocturno++;
      minutosAcumulados++;
    } else {
      if (esDiurno) extraDiurna++; else extraNocturna++;
    }
    cursor++;
  }

  return {
    ordinarioDiurno, ordinarioNocturno,
    extraDiurna, extraNocturna,
    extraFestDiurna, extraFestNocturna,
    totalTrabajado: ordinarioDiurno+ordinarioNocturno+extraDiurna+extraNocturna+extraFestDiurna+extraFestNocturna,
  };
}

function calcularLiquidacionMes(emp, registros, mes) {
  const regsAprobados = registros.filter(r => r.empId===emp.id && r.estado==="aprobado" && !r.eliminado && r.fecha.startsWith(mes));
  const vh = Math.round(emp.salario / 210);    // valor hora
  const vd = Math.round(emp.salario / 30);     // valor día
  const vm = Math.round(vh / 60);              // valor minuto

  let totOrdDiurno=0, totOrdNoct=0, totExtraDiurna=0, totExtraNoct=0;
  let totExtraFestDiurna=0, totExtraFestNocturna=0;
  let totDescMinutos=0, totDescDias=0, totIncapDias=0;
  const detalleDias = [];

  // Contar incapacidades para calcular quién paga
  const incapRegs = regsAprobados.filter(r=>r.bloques.some(b=>b.tipo==="incapacidad"));
  let incapContador = 0;

  regsAprobados.slice().sort((a,b)=>a.fecha.localeCompare(b.fecha)).forEach(reg => {
    const tipoEspecial = reg.bloques[0]?.tipo;

    if (tipoEspecial==="descanso_dia") {
      detalleDias.push({ fecha:reg.fecha, tipo:"descanso" });
      return;
    }
    if (tipoEspecial==="no_asistio") {
      totDescDias += 1;
      detalleDias.push({ fecha:reg.fecha, tipo:"inasistencia", descValor:vd });
      return;
    }
    if (tipoEspecial==="incapacidad") {
      incapContador++;
      const esMpleador = incapContador <= 2;
      const valorIncap = esMpleador ? Math.round(vd*0.6667) : 0; // empleador paga 66.67% días 1-2
      totIncapDias++;
      detalleDias.push({ fecha:reg.fecha, tipo:"incapacidad", diaNum:incapContador, esEmpleador:esMpleador, valorIncap });
      return;
    }

    // Día trabajado normal
    const d = calcularMinutosDia(reg.bloques, reg.fecha);
    totOrdDiurno      += d.ordinarioDiurno;
    totOrdNoct        += d.ordinarioNocturno;
    totExtraDiurna    += d.extraDiurna;
    totExtraNoct      += d.extraNocturna;
    totExtraFestDiurna   += d.extraFestDiurna;
    totExtraFestNocturna += d.extraFestNocturna;

    // Detectar tiempo faltante
    const esSab = esSabado(reg.fecha);
    const jornadaEsperada = esSab ? 270 : 450;
    const totalTrabajado = d.totalTrabajado;
    const faltante = jornadaEsperada - (d.ordinarioDiurno + d.ordinarioNocturno);
    if (faltante > 0 && totalTrabajado > 0) {
      totDescMinutos += faltante;
      detalleDias.push({ fecha:reg.fecha, tipo:"trabajo", ...d, faltanteMin:faltante, faltanteVal:Math.round((faltante/60)*vh) });
    } else {
      detalleDias.push({ fecha:reg.fecha, tipo:"trabajo", ...d, faltanteMin:0, faltanteVal:0 });
    }
  });

  // ── Compensación de tiempo faltante con horas extra (desde septiembre 2026) ──
  // Antes de septiembre de 2026 el tiempo faltante se descontaba directamente
  // (comportamiento histórico, intacto — no se recalculan meses ya cerrados).
  // Desde septiembre de 2026: el tiempo faltante del mes primero se compensa con
  // las horas extra ORDINARIAS (diurna y nocturna de días normales — las festivas
  // y dominicales NO se usan, son un concepto distinto), dándole prioridad a la
  // extra diurna (más económica) antes que a la nocturna. Solo lo que no alcance
  // a cubrirse con esas extras se sigue descontando como "tiempo faltante".
  const COMPENSAR_FALTANTE_DESDE = '2026-09';
  const compensarActivo = mes >= COMPENSAR_FALTANTE_DESDE;

  let extraDiurnaConsumida = 0, extraNoctConsumida = 0, minutosCompensados = 0;
  let totDescMinutosFinal = totDescMinutos;

  if (compensarActivo && totDescMinutos > 0) {
    const poolOrdinaria = totExtraDiurna + totExtraNoct;
    minutosCompensados = Math.min(totDescMinutos, poolOrdinaria);

    let restante = minutosCompensados;
    extraDiurnaConsumida = Math.min(restante, totExtraDiurna);
    restante -= extraDiurnaConsumida;
    extraNoctConsumida = Math.min(restante, totExtraNoct);

    totDescMinutosFinal = totDescMinutos - minutosCompensados;
  }

  const totExtraDiurnaPagada = totExtraDiurna - extraDiurnaConsumida;
  const totExtraNoctPagada   = totExtraNoct - extraNoctConsumida;

  // Valores extras (sobre los minutos ya netos de compensación)
  const valorExtraDiurna      = Math.round((totExtraDiurnaPagada/60)*vh*1.25);
  const valorExtraNoct        = Math.round((totExtraNoctPagada/60)*vh*1.75);
  const valorExtraFestDiurna  = Math.round((totExtraFestDiurna/60)*vh*2.00);
  const valorExtraFestNoct    = Math.round((totExtraFestNocturna/60)*vh*2.50);
  const valorRecNocturno      = Math.round((totOrdNoct/60)*vh*0.35);
  const totalExtras = valorExtraDiurna+valorExtraNoct+valorExtraFestDiurna+valorExtraFestNoct+valorRecNocturno;

  // Descuentos
  const descInasistencia = totDescDias * vd;
  const descMinutos = Math.round((totDescMinutosFinal/60)*vh);
  const totalDescuentosLaborales = descInasistencia + descMinutos;

  const auxTransporte         = 249095;
  const totalDevengado        = emp.salario + totalExtras + auxTransporte - totalDescuentosLaborales;
  const baseSegSocial         = emp.salario + totalExtras - totalDescuentosLaborales;

  // ── Piso y techo legal del IBC (desde septiembre 2026) ──────────────────────
  // Antes de septiembre de 2026 la base de salud/pensión se dejaba tal cual
  // (comportamiento histórico, intacto — no se recalculan meses ya cerrados).
  // Desde septiembre de 2026: el IBC no puede ser inferior a 1 SMLMV ni superior
  // a 25 SMLMV, según la ley colombiana vigente.
  const IBC_AJUSTE_DESDE = '2026-09';
  const ibcAjusteActivo  = mes >= IBC_AJUSTE_DESDE;
  const SMLMV_2026       = 1750905;
  const IBC_PISO         = SMLMV_2026;       // 1 SMLMV
  const IBC_TECHO        = SMLMV_2026 * 25;  // 25 SMLMV

  let baseIBC = Math.max(0, baseSegSocial);
  if (ibcAjusteActivo && baseIBC > 0) {
    baseIBC = Math.min(IBC_TECHO, Math.max(IBC_PISO, baseIBC));
  }

  const descSalud              = Math.round(baseIBC*0.04);
  const descPension            = Math.round(baseIBC*0.04);
  const neto                   = totalDevengado - descSalud - descPension;

  return {
    emp, mes, vh, vd,
    mins: { ordDiurno:totOrdDiurno, ordNoct:totOrdNoct, extraDiurna:totExtraDiurnaPagada, extraNoct:totExtraNoctPagada, extraFestDiurna:totExtraFestDiurna, extraFestNoct:totExtraFestNocturna },
    valores: { extraDiurna:valorExtraDiurna, extraNoct:valorExtraNoct, extraFestDiurna:valorExtraFestDiurna, extraFestNoct:valorExtraFestNoct, recNocturno:valorRecNocturno, auxTransporte },
    descuentosLaborales: { inasistencias:descInasistencia, minutos:descMinutos, total:totalDescuentosLaborales, diasDesc:totDescDias, minsDesc:totDescMinutosFinal },
    descuentos: { salud:descSalud, pension:descPension },
    totales: { devengado:totalDevengado, descuentos:descSalud+descPension, neto },
    diasAprobados: regsAprobados.length,
    detalleDias,
    compensacion: {
      activa: compensarActivo,
      minutosFaltanteOriginal: totDescMinutos,
      minutosCompensados,
      minutosDescubiertos: totDescMinutosFinal,
      extraDiurnaConsumida,
      extraNoctConsumida,
    },
    ibc: {
      activo: ibcAjusteActivo,
      baseOriginal: Math.max(0, baseSegSocial),
      baseAjustada: baseIBC,
      ajustadaPiso: ibcAjusteActivo && baseIBC===IBC_PISO && Math.max(0,baseSegSocial)<IBC_PISO,
      ajustadaTecho: ibcAjusteActivo && baseIBC===IBC_TECHO && Math.max(0,baseSegSocial)>IBC_TECHO,
    },
  };
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff;color:#111;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif}
  ::-webkit-scrollbar{width:4px}
  ::-webkit-scrollbar-track{background:#f0f0f0}
  ::-webkit-scrollbar-thumb{background:#bbb;border-radius:2px}
  .ad-app{min-height:100vh;background:#fff}
  .login-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f0f0f0;padding:2rem}
  .login-card{width:100%;max-width:420px;background:#fff;box-shadow:0 4px 24px rgba(0,0,0,0.08);border:1px solid #e0e0e0;border-radius:16px;padding:3rem 2.5rem;position:relative;overflow:hidden}
  .login-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:#111}
  .login-logo{text-align:center;margin-bottom:2.5rem}
  .login-logo-name{font-family:'Cormorant Garamond',serif;font-size:2rem;font-weight:300;letter-spacing:0.15em;color:#111;line-height:1}
  .login-logo-sub{font-size:10px;letter-spacing:0.3em;text-transform:uppercase;color:#555;margin-top:6px}
  .login-divider{width:40px;height:1px;background:#111;margin:1rem auto}
  .login-title{font-size:13px;letter-spacing:0.15em;text-transform:uppercase;color:#555;text-align:center;margin-bottom:2rem}
  .form-group{margin-bottom:1.25rem}
  .form-label{display:block;font-size:10px;letter-spacing:0.2em;text-transform:uppercase;color:#555;margin-bottom:8px}
  .form-input{width:100%;background:#f4f4f4;border:1px solid #e0e0e0;color:#111;padding:12px 16px;font-size:14px;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;outline:none;transition:border-color 0.2s}
  .form-input:focus{border-color:#111}
  .form-input::placeholder{color:#999}
  .btn-primary{width:100%;background:#111;color:#fff;border:none;padding:13px;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;font-weight:500;cursor:pointer;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;transition:background 0.2s;margin-top:0.5rem}
  .btn-primary:hover{background:#333}
  .login-error{background:rgba(211,47,47,0.08);border:1px solid rgba(211,47,47,0.25);color:#D32F2F;font-size:12px;padding:10px 14px;border-radius:1px;margin-bottom:1rem;text-align:center}
  .layout{display:flex;height:100vh}
  .sidebar{width:240px;min-width:240px;background:#fff;border-right:1px solid #e0e0e0;display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow-y:auto}
  .sidebar-logo{padding:2rem 1.5rem 1.5rem;border-bottom:1px solid #e0e0e0}
  .sidebar-logo-name{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:400;letter-spacing:0.1em;color:#111;line-height:1.1}
  .sidebar-logo-sub{font-size:9px;letter-spacing:0.25em;text-transform:uppercase;color:#999;margin-top:4px}
  .sidebar-nav{padding:1.5rem 0;flex:1}
  .nav-section-label{font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:#999;padding:0 1.5rem;margin-bottom:6px;margin-top:1.5rem}
  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 1rem;margin:2px 8px;font-size:13px;color:#555;cursor:pointer;transition:all 0.15s;border-left:none;border-radius:8px}
  .nav-item:hover{color:#111;background:#f0f0f0}
  .nav-item.active{color:#111;border-left-color:transparent;background:rgba(0,0,0,0.07);font-weight:500}
  .nav-icon{font-size:16px;width:20px;text-align:center}
  .nav-badge{margin-left:auto;background:#D32F2F;color:#fff;font-size:10px;font-weight:500;padding:1px 7px;border-radius:10px;min-width:18px;text-align:center}
  .sidebar-footer{padding:1.5rem;border-top:1px solid #e0e0e0}
  .user-info{display:flex;align-items:center;gap:10px}
  .user-avatar{width:32px;height:32px;border-radius:50%;background:#111;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:#fff;flex-shrink:0}
  .user-name{font-size:12px;font-weight:500;color:#111}
  .user-role{font-size:10px;color:#555}
  .btn-logout{background:none;border:none;color:#999;cursor:pointer;font-size:11px;padding:4px 8px;border-radius:2px;transition:color 0.15s;margin-left:auto;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif}
  .btn-logout:hover{color:#D32F2F}
  .main{flex:1;overflow-y:auto;background:#f8f8f8}
  .main-header{padding:2rem 2.5rem 1.5rem;border-bottom:1px solid #e0e0e0;display:flex;align-items:flex-end;justify-content:space-between;background:#fff}
  .page-title{font-family:'Cormorant Garamond',serif;font-size:1.8rem;font-weight:300;color:#111;letter-spacing:0.05em}
  .page-sub{font-size:12px;color:#555;margin-top:4px}
  .main-content{padding:2rem 2.5rem}
  .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;margin-bottom:2rem}
  .stat-card{background:#fff;border:1px solid #e0e0e0;border-radius:12px;padding:1.25rem 1.5rem;position:relative;overflow:hidden}
  .stat-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:var(--accent,#111)}
  .stat-label{font-size:9px;letter-spacing:0.25em;text-transform:uppercase;color:#999;margin-bottom:8px}
  .stat-value{font-size:2rem;font-weight:600;color:#111;line-height:1;font-variant-numeric:tabular-nums}
  .stat-sub{font-size:11px;color:#999;margin-top:4px}
  .table-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem}
  .table-title{font-size:13px;letter-spacing:0.1em;text-transform:uppercase;color:#555}
  .btn-add{background:#111;color:#fff;border:none;padding:8px 18px;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;font-weight:500;cursor:pointer;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;transition:background 0.2s;display:flex;align-items:center;gap:6px}
  .btn-add:hover{background:#333}
  .btn-sec{background:transparent;color:#555;border:1px solid #e0e0e0;padding:8px 18px;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;transition:all 0.2s;display:flex;align-items:center;gap:6px}
  .btn-sec:hover{border-color:#111;color:#111}
  .btn-danger{background:transparent;color:#D32F2F;border:1px solid rgba(211,47,47,0.3);padding:8px 18px;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;transition:all 0.2s;display:inline-flex;align-items:center;gap:6px}
  .btn-danger:hover{background:rgba(211,47,47,0.06)}
  .search-bar{display:flex;gap:1rem;margin-bottom:1rem;align-items:center}
  .search-input{flex:1;background:#fff;border:1px solid #e0e0e0;color:#111;padding:9px 14px;font-size:13px;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:1px;outline:none;transition:border-color 0.2s}
  .search-input:focus{border-color:#111}
  .search-input::placeholder{color:#999}
  .data-table{width:100%;border-collapse:collapse}
  .data-table th{font-size:9px;letter-spacing:0.25em;text-transform:uppercase;color:#999;padding:10px 16px;text-align:left;border-bottom:1px solid #e0e0e0;font-weight:400;background:#fff}
  .data-table td{padding:13px 16px;font-size:13px;color:#111;border-bottom:1px solid #e0e0e0;vertical-align:middle;background:#fff}
  .data-table tr:hover td{background:#fafafa}
  .emp-avatar{width:32px;height:32px;border-radius:50%;background:#111;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;font-weight:500;flex-shrink:0}
  .emp-name-cell{display:flex;align-items:center;gap:10px}
  .emp-name{font-weight:500}
  .emp-id{font-size:11px;color:#999}
  .badge{display:inline-block;font-size:10px;padding:3px 9px;border-radius:20px;letter-spacing:0.05em;font-weight:500}
  .badge-active,.badge-activo{background:rgba(46,125,82,0.1);color:#2E7D52;border:1px solid rgba(46,125,82,0.2)}
  .badge-inactive,.badge-inactivo{background:rgba(0,0,0,0.05);color:#999;border:1px solid #e0e0e0}
  .badge-ceo{background:rgba(0,0,0,0.08);color:#111;border:1px solid rgba(0,0,0,0.2)}
  .badge-rrhh{background:rgba(21,101,192,0.1);color:#1565C0;border:1px solid rgba(21,101,192,0.2)}
  .badge-emp,.badge-empleado{background:rgba(0,0,0,0.05);color:#555;border:1px solid #e0e0e0}
  .badge-pendiente{background:rgba(230,81,0,0.1);color:#E65100;border:1px solid rgba(230,81,0,0.25)}
  .badge-aprobado{background:rgba(46,125,82,0.1);color:#2E7D52;border:1px solid rgba(46,125,82,0.2)}
  .badge-rechazado{background:rgba(211,47,47,0.1);color:#D32F2F;border:1px solid rgba(211,47,47,0.2)}
  .action-btns{display:flex;gap:6px}
  .btn-icon{background:none;border:1px solid #e0e0e0;color:#555;width:28px;height:28px;border-radius:1px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;transition:all 0.15s}
  .btn-icon:hover{border-color:#111;color:#111}
  .btn-icon.danger:hover{border-color:#D32F2F;color:#D32F2F}
  .empty-state{text-align:center;padding:4rem 2rem;color:#999;font-size:13px}
  .empty-icon{font-size:2rem;margin-bottom:1rem;opacity:0.3}
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:2rem;backdrop-filter:blur(4px)}
  .modal{background:#fff;border:1px solid #e0e0e0;border-radius:16px;width:100%;max-width:600px;max-height:90vh;overflow-y:auto;position:relative;box-shadow:0 16px 48px rgba(0,0,0,0.15)}
  .modal::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:#111}
  .modal-header{padding:1.75rem 2rem 1.25rem;border-bottom:1px solid #e0e0e0;display:flex;align-items:center;justify-content:space-between}
  .modal-title{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:300;color:#111}
  .modal-close{background:none;border:none;color:#999;cursor:pointer;font-size:18px;transition:color 0.15s;line-height:1}
  .modal-close:hover{color:#111}
  .modal-body{padding:1.75rem 2rem}
  .modal-footer{padding:1.25rem 1.25rem;border-top:1px solid #e0e0e0;display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
  .form-group-full{grid-column:span 2}
  .form-section{font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:#555;margin:1.5rem 0 1rem;padding-bottom:6px;border-bottom:1px solid #e0e0e0;grid-column:span 2}
  .form-select{width:100%;background:#f4f4f4;border:1px solid #e0e0e0;color:#111;padding:12px 16px;font-size:14px;font-family:'Century Gothic','CenturyGothic','AppleGothic','Gill Sans',sans-serif;border-radius:8px;outline:none;transition:border-color 0.2s;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;padding-right:36px}
  .form-select:focus{border-color:#111}
  .file-upload-area{border:1px dashed #e0e0e0;padding:1.5rem;text-align:center;border-radius:1px;cursor:pointer;transition:all 0.2s;color:#999;font-size:12px}
  .file-upload-area:hover{border-color:#111;color:#555}
  .file-tag{display:inline-flex;align-items:center;gap:6px;background:rgba(0,0,0,0.05);border:1px solid rgba(0,0,0,0.12);color:#555;font-size:11px;padding:4px 10px;border-radius:1px;margin:4px}
  .file-tag-remove{cursor:pointer;opacity:0.5}
  .file-tag-remove:hover{opacity:1}
  .detail-header{display:flex;align-items:center;gap:1.5rem;margin-bottom:2rem;padding-bottom:1.5rem;border-bottom:1px solid #e0e0e0}
  .detail-avatar{width:64px;height:64px;border-radius:50%;background:#111;display:flex;align-items:center;justify-content:center;font-size:22px;color:#fff;font-weight:500;flex-shrink:0}
  .detail-name{font-family:'Cormorant Garamond',serif;font-size:1.6rem;font-weight:300;color:#111}
  .detail-meta{font-size:12px;color:#555;margin-top:4px}
  .info-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-bottom:1.5rem}
  .info-card{background:#fff;border:1px solid #e0e0e0;border-radius:10px;padding:1rem 1.25rem}
  .info-label{font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#999;margin-bottom:6px}
  .info-value{font-size:13px;color:#111}
  .docs-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
  .doc-card{background:#fff;border:1px solid #e0e0e0;border-radius:1px;padding:1rem 1.25rem;display:flex;align-items:center;gap:10px;cursor:pointer;transition:border-color 0.15s}
  .doc-card:hover{border-color:#111}
  .toast{position:fixed;bottom:2rem;right:2rem;background:#fff;border:1px solid #e0e0e0;border-radius:12px;padding:12px 18px;font-size:13px;color:#111;z-index:2000;display:flex;align-items:center;gap:10px;box-shadow:0 8px 32px rgba(0,0,0,0.12);animation:slideIn 0.2s ease}
  .toast.success{border-left:3px solid #2E7D52}
  .toast.error{border-left:3px solid #D32F2F}
  @keyframes slideIn{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
  .tabs{display:flex;gap:0;border-bottom:1px solid #e0e0e0;margin-bottom:1.5rem;flex-wrap:wrap}
  .mobile-tab-select{display:none}
  .tab{padding:10px 20px;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:#999;cursor:pointer;border-bottom:2px solid transparent;transition:all 0.15s;margin-bottom:-1px;white-space:nowrap}
  .tab.active{color:#111;border-bottom-color:#111}
  .tab:hover{color:#555}
  .alert-box{background:rgba(230,81,0,0.06);border:1px solid rgba(230,81,0,0.2);border-radius:10px;padding:1rem 1.25rem;margin-bottom:1rem;font-size:13px;color:#E65100;display:flex;align-items:flex-start;gap:8px}
  .info-box{background:rgba(21,101,192,0.06);border:1px solid rgba(21,101,192,0.2);border-radius:10px;padding:1rem 1.25rem;margin-bottom:1rem;font-size:13px;color:#1565C0;display:flex;align-items:flex-start;gap:8px}

  /* HORARIO BLOQUES */
  .bloque-row{display:grid;grid-template-columns:auto 1fr 1fr auto;gap:8px;align-items:center;padding:8px 12px;border:1px solid #e0e0e0;border-radius:1px;margin-bottom:6px}
  .bloque-row.descanso{background:#fafafa;border-style:dashed}
  .bloque-tipo{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;font-weight:500;min-width:70px}
  .bloque-tipo.trabajo{color:#111}
  .bloque-tipo.descanso{color:#999}

  /* RESUMEN HORAS */
  .resumen-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin:1rem 0}
  .rh-card{background:#f8f8f8;border:1px solid #e0e0e0;border-radius:1px;padding:8px 12px}
  .rh-label{font-size:9px;letter-spacing:0.15em;text-transform:uppercase;color:#999;margin-bottom:4px}
  .rh-value{font-size:15px;font-weight:500;color:#111}

  /* HORARIO CARD */
  .horario-card{background:#fff;border:1px solid #e0e0e0;border-radius:12px;padding:1.25rem 1.5rem;margin-bottom:10px}
  .horario-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:10px;gap:1rem}
  .horario-fecha{font-weight:500;font-size:14px;color:#111}
  .horario-emp{font-size:12px;color:#555;margin-top:2px}
  .horario-tags{display:flex;flex-wrap:wrap;gap:4px;margin:8px 0}
  .htag{font-size:11px;padding:2px 8px;border-radius:1px;font-variant-numeric:tabular-nums}
  .htag-ord{background:#f0f0f0;color:#555;border:1px solid #e0e0e0}
  .htag-noct{background:rgba(21,101,192,0.08);color:#1565C0;border:1px solid rgba(21,101,192,0.2)}
  .htag-extra{background:rgba(230,81,0,0.08);color:#E65100;border:1px solid rgba(230,81,0,0.2)}
  .htag-xnoct{background:rgba(211,47,47,0.08);color:#D32F2F;border:1px solid rgba(211,47,47,0.2)}

  /* NOMINA */
  .nom-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-bottom:1.5rem}
  .nom-card{background:#fff;border:1px solid #e0e0e0;border-radius:12px;padding:1.25rem 1.5rem;position:relative;overflow:hidden}
  .nom-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:var(--nc,#111)}
  .nom-label{font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#999;margin-bottom:6px}
  .nom-value{font-size:1.5rem;font-weight:600;color:#111;line-height:1;font-variant-numeric:tabular-nums}
  .liq-table{width:100%;border-collapse:collapse;margin-bottom:1.5rem}
  .liq-table th{font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#999;padding:8px 14px;text-align:left;border-bottom:1px solid #e0e0e0;font-weight:400;background:#fff}
  .liq-table td{padding:11px 14px;font-size:13px;color:#111;border-bottom:1px solid #f0f0f0;background:#fff;vertical-align:middle}
  .liq-table tr:hover td{background:#fafafa}
  .liq-table tr.total-row td{font-weight:500;border-top:2px solid #111;border-bottom:none;background:#fafafa}
  .liq-table tr.neto-row td{font-size:15px;font-weight:500;color:#2E7D52;border-top:none;background:#f0faf5}
  .desp-wrap{background:#fff;border:1px solid #e0e0e0;border-radius:2px;max-width:600px;margin:0 auto;padding:2.5rem}
  .desp-header{border-bottom:2px solid #111;padding-bottom:1.25rem;margin-bottom:1.5rem;display:flex;justify-content:space-between;align-items:flex-end}
  .desp-empresa{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:400;letter-spacing:0.1em}
  .desp-title{font-size:10px;letter-spacing:0.25em;text-transform:uppercase;color:#999;margin-top:4px}
  .desp-periodo{font-size:12px;color:#555;text-align:right}
  .desp-emp{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:1.5rem;padding:1rem;background:#f8f8f8;border-radius:1px}
  .desp-field{font-size:12px}
  .desp-field span{color:#999;font-size:10px;text-transform:uppercase;letter-spacing:0.1em;display:block;margin-bottom:2px}
  .desp-items{width:100%;border-collapse:collapse;margin-bottom:1rem}
  .desp-items th{font-size:10px;letter-spacing:0.15em;text-transform:uppercase;color:#999;padding:8px 10px;text-align:left;border-bottom:1px solid #e0e0e0;font-weight:400}
  .desp-items td{padding:9px 10px;font-size:12px;border-bottom:1px solid #f0f0f0}
  .desp-items tr.section-head td{font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#999;background:#f8f8f8;padding:6px 10px}
  .desp-items tr.subtotal td{font-weight:500;border-top:1px solid #e0e0e0;background:#fafafa}
  .desp-items tr.neto td{font-weight:500;font-size:14px;color:#2E7D52;border-top:2px solid #111;background:#f0faf5}
  .desp-firma{display:grid;grid-template-columns:1fr 1fr;gap:2rem;margin-top:2rem;padding-top:1.5rem;border-top:1px solid #e0e0e0}
  .firma-line{border-top:1px solid #111;margin-top:2rem;padding-top:6px;font-size:10px;color:#999;text-align:center}

  /* BARRA DE PROGRESO */
  .barra-wrap{margin:10px 0 4px;border-radius:2px;overflow:hidden;height:28px;display:flex;background:transparent;position:relative}
  .barra-seg{height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:500;color:#fff;overflow:hidden;white-space:nowrap;transition:width 0.3s}
  .barra-ord-d{background:#90CAF9}
  .barra-ord-n{background:#1565C0}
  .barra-ext-d{background:#FFC107}
  .barra-ext-n{background:#E53935}
  .barra-fest-d{background:#9C27B0}
  .barra-fest-n{background:#4A148C}
  .barra-labels{display:flex;flex-wrap:wrap;gap:6px 12px;margin-top:6px;font-size:11px}
  .barra-label{display:flex;align-items:center;gap:4px}
  .barra-dot{width:10px;height:10px;border-radius:1px;flex-shrink:0}
  .barra-calc{font-size:11px;color:#555;margin-top:4px;line-height:1.7;padding:6px 8px;background:#f8f8f8;border-radius:1px;border:1px solid #e0e0e0}

  /* ── MÓVIL ── */
  .mobile-header{display:none}
  .bottom-nav{display:none}
  @media(max-width:768px){
    .sidebar{display:none}
    .layout{flex-direction:column;height:auto;min-height:100vh}
    .main{padding-bottom:80px;overflow-y:visible}
    .main-header{padding:1rem 1.25rem;position:sticky;top:0;z-index:100;background:#fff}
    .main-content{padding:1rem 1.25rem}
    .stats-grid{grid-template-columns:repeat(2,1fr)}
    .tabs{display:none}
    .mobile-tab-select{display:block}
    .form-grid{grid-template-columns:1fr}
    .form-group-full,.form-section{grid-column:span 1}
    .info-grid{grid-template-columns:repeat(2,1fr)}
    .nom-grid{grid-template-columns:1fr}
    .page-title{font-size:1.4rem}

    /* Header móvil */
    .mobile-header{
      display:flex;align-items:center;justify-content:space-between;
      padding:12px 16px;background:#fff;border-bottom:1px solid #e0e0e0;
      position:sticky;top:0;z-index:200;
    }
    .mobile-header-logo{height:32px;object-fit:contain}
    .mobile-header-user{display:flex;align-items:center;gap:8px;font-size:12px;color:#555}
    .mobile-header-avatar{width:30px;height:30px;border-radius:50%;background:#111;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;font-weight:500;flex-shrink:0}

    /* Bottom nav */
    .bottom-nav{
      display:flex;position:fixed;bottom:0;left:0;right:0;
      background:#fff;border-top:1px solid #e0e0e0;
      z-index:200;padding:0;
    }
    .bottom-nav-item{
      flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
      padding:8px 4px;cursor:pointer;transition:all 0.15s;
      border:none;background:none;font-family:inherit;
      color:#999;font-size:9px;letter-spacing:0.05em;text-transform:uppercase;gap:3px;
    }
    .bottom-nav-item.active{color:#111}
    .bottom-nav-icon{font-size:20px;line-height:1}
    .bottom-nav-badge{
      position:absolute;top:4px;right:calc(50% - 18px);
      background:#D32F2F;color:#fff;font-size:9px;font-weight:700;
      padding:1px 5px;border-radius:10px;min-width:16px;text-align:center;
    }
    .bottom-nav-item{position:relative}

    /* Tablas en móvil */
    .data-table thead{display:none}
    .data-table tr{display:block;border-bottom:1px solid #e0e0e0;padding:10px 0}
    .data-table td{display:flex;justify-content:space-between;padding:4px 16px;border:none;font-size:13px}
    .liq-table thead{display:none}
    .liq-table tr{display:block;border:1px solid #e0e0e0;border-radius:8px;margin-bottom:8px;padding:10px}
    .liq-table td{display:flex;justify-content:space-between;padding:3px 0;border:none;font-size:12px}
    .liq-table tr.total-row td{display:flex;justify-content:space-between}
  }
    .form-grid{grid-template-columns:1fr}
    .form-group-full,.form-section{grid-column:span 1}
    .info-grid{grid-template-columns:repeat(2,1fr)}
    .main-content{padding:1.5rem}
    .nom-grid{grid-template-columns:1fr}
  }
`;

const ROLES_LABEL = { ceo:"CEO", rrhh:"RRHH", empleado:"Empleado" };
const CONTRATOS = ["Indefinido","Fijo","Obra o labor","Aprendizaje"];
const JORNADAS = [42,44,40,36];

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, []);
  return <div className={`toast ${type}`}><span>{type==="success"?"✓":"✕"}</span>{msg}</div>;
}


// ── Reset Password Modal (CEO) ────────────────────────────────────────────────
function ResetPasswordModal({ emp, onSave, onClose }) {
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const handleSave = () => {
    if (newPass.length < 4) { setError("Mínimo 4 caracteres"); return; }
    if (newPass !== confirm) { setError("Las contraseñas no coinciden"); return; }
    onSave(newPass);
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:420}}>
        <div className="modal-header"><h2 className="modal-title">Resetear contraseña</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{fontSize:13,color:"#555",marginBottom:"1.25rem"}}>Asigna una contraseña temporal para <strong>{emp.nombre}</strong>. El empleado deberá cambiarla en su próximo inicio de sesión.</div>
          {error&&<div className="login-error">{error}</div>}
          <div className="form-group"><label className="form-label">Nueva contraseña temporal</label><input className="form-input" type="password" value={newPass} onChange={e=>{setNewPass(e.target.value);setError("");}} placeholder="Mínimo 4 caracteres" /></div>
          <div className="form-group"><label className="form-label">Confirmar contraseña</label><input className="form-input" type="password" value={confirm} onChange={e=>{setConfirm(e.target.value);setError("");}} placeholder="Repite la contraseña" /></div>
        </div>
        <div className="modal-footer"><button className="btn-sec" onClick={onClose}>Cancelar</button><button className="btn-add" onClick={handleSave}>Guardar contraseña</button></div>
      </div>
    </div>
  );
}

// ── Change Password Modal (Empleado — primer login) ───────────────────────────
function ChangePasswordModal({ user, onSave }) {
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const handleSave = () => {
    if (newPass.length < 4) { setError("Mínimo 4 caracteres"); return; }
    if (newPass !== confirm) { setError("Las contraseñas no coinciden"); return; }
    onSave(newPass);
  };
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo"><div className="login-logo-name">Angela Duran</div><div className="login-divider"/><div className="login-logo-sub">Sistema de Nómina</div></div>
        <div className="login-title">Cambia tu contraseña</div>
        <div style={{background:"rgba(230,81,0,0.06)",border:"1px solid rgba(230,81,0,0.2)",borderRadius:1,padding:"10px 14px",marginBottom:"1.25rem",fontSize:12,color:"#E65100",textAlign:"center"}}>
          Hola <strong>{user.nombre.split(" ")[0]}</strong> — por seguridad debes crear una contraseña personal antes de continuar.
        </div>
        {error&&<div className="login-error">{error}</div>}
        <div className="form-group"><label className="form-label">Nueva contraseña</label><input className="form-input" type="password" value={newPass} onChange={e=>{setNewPass(e.target.value);setError("");}} placeholder="Mínimo 4 caracteres" autoFocus /></div>
        <div className="form-group"><label className="form-label">Confirmar contraseña</label><input className="form-input" type="password" value={confirm} onChange={e=>{setConfirm(e.target.value);setError("");}} placeholder="Repite la contraseña" onKeyDown={e=>e.key==="Enter"&&handleSave()} /></div>
        <button className="btn-primary" onClick={handleSave}>Guardar y continuar →</button>
      </div>
    </div>
  );
}

// ── Employee Modal ────────────────────────────────────────────────────────────
function EmployeeModal({ emp, onSave, onClose }) {
  const blank = { cedula:"",nombre:"",cargo:"",salario:1750905,jornada:42,telefono:"",email:"",direccion:"",fechaNacimiento:"",fechaIngreso:"",contrato:"Indefinido",estado:"activo",rol:"empleado",docs:[],notas:"",usuario:"",password:"",mustChangePassword:true };
  const [form, setForm] = useState(emp || blank);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleSubmit = () => {
    if (!form.nombre||!form.cedula) return alert("Nombre y cédula son obligatorios");
    if (!emp && (!form.usuario||!form.password)) return alert("Usuario y contraseña son obligatorios para el nuevo empleado");
    onSave(form);
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header"><h2 className="modal-title">{emp?"Editar empleado":"Nuevo empleado"}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-section">Información personal</div>
            <div className="form-group"><label className="form-label">Nombre completo *</label><input className="form-input" value={form.nombre} onChange={e=>set("nombre",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Cédula *</label><input className="form-input" value={form.cedula} onChange={e=>set("cedula",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Fecha de nacimiento</label><input className="form-input" type="date" value={form.fechaNacimiento} onChange={e=>set("fechaNacimiento",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono} onChange={e=>set("telefono",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>set("email",e.target.value)} /></div>
            <div className="form-group form-group-full"><label className="form-label">Dirección</label><input className="form-input" value={form.direccion} onChange={e=>set("direccion",e.target.value)} /></div>
            <div className="form-section">Información laboral</div>
            <div className="form-group"><label className="form-label">Cargo</label><input className="form-input" value={form.cargo} onChange={e=>set("cargo",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Fecha de ingreso</label><input className="form-input" type="date" value={form.fechaIngreso} onChange={e=>set("fechaIngreso",e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Salario mensual</label><input className="form-input" type="number" value={form.salario} onChange={e=>set("salario",Number(e.target.value))} /></div>
            <div className="form-group"><label className="form-label">Tipo de contrato</label><select className="form-select" value={form.contrato} onChange={e=>set("contrato",e.target.value)}>{CONTRATOS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Rol en el sistema</label><select className="form-select" value={form.rol} onChange={e=>set("rol",e.target.value)}><option value="empleado">Empleado</option><option value="rrhh">RRHH</option><option value="ceo">CEO</option></select></div>
            <div className="form-group"><label className="form-label">Estado</label><select className="form-select" value={form.estado} onChange={e=>set("estado",e.target.value)}><option value="activo">Activo</option><option value="inactivo">Inactivo</option></select></div>
            <div className="form-section">Acceso al sistema</div>
            <div className="form-group"><label className="form-label">Usuario *</label><input className="form-input" value={form.usuario} onChange={e=>set("usuario",e.target.value)} placeholder="Ej: maria.garcia" /></div>
            <div className="form-group"><label className="form-label">Contraseña *</label><input className="form-input" type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="Mínimo 6 caracteres" /></div>
            <div className="form-section">Documentos</div>
            <div className="form-group form-group-full">
              <label className="form-label">Archivos PDF</label>
              <div className="file-upload-area" onClick={()=>{ const n=prompt("Nombre del documento:"); if(n) set("docs",[...form.docs,n]); }}>📎 Clic para adjuntar PDF</div>
              <div style={{marginTop:8}}>{form.docs.map((d,i)=><span key={i} className="file-tag">📄 {d}<span className="file-tag-remove" onClick={()=>set("docs",form.docs.filter((_,j)=>j!==i))}>✕</span></span>)}</div>
            </div>
            <div className="form-group form-group-full"><label className="form-label">Notas internas</label><textarea className="form-input" rows={3} value={form.notas} onChange={e=>set("notas",e.target.value)} style={{resize:"vertical"}} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn-sec" onClick={onClose}>Cancelar</button><button className="btn-add" onClick={handleSubmit}>{emp?"Guardar cambios":"Crear empleado"}</button></div>
      </div>
    </div>
  );
}

// ── Employee Detail ───────────────────────────────────────────────────────────
function EmployeeDetail({ emp, onBack, onEdit, onReset, desprendibles, setDesprendibles, showToast, refreshDesp }) {
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const [subiendo, setSubiendo] = useState(false);
  const [certLabel, setCertLabel] = useState("");
  const [epsLabel, setEpsLabel] = useState("");
  const hoy = new Date();
  const [despMesNum, setDespMesNum] = useState(String(hoy.getMonth()+1).padStart(2,"0"));
  const [despAnio, setDespAnio] = useState(String(hoy.getFullYear()));
  const despMes = despAnio + "-" + despMesNum;

  // Documentos existentes por categoría
  const misDesp = Object.entries(desprendibles||{})
    .filter(([k])=>k.startsWith(emp.id+"_desp_"))
    .map(([k,url])=>{ const mes=k.replace(emp.id+"_desp_",""); const [a,m]=mes.split("-"); return {k,url,mes,label:meses[parseInt(m)-1]+" "+a}; })
    .sort((a,b)=>b.mes.localeCompare(a.mes));
  const misCert = Object.entries(desprendibles||{})
    .filter(([k])=>k.startsWith(emp.id+"_cert_"))
    .map(([k,url])=>({k,url,label:k.replace(emp.id+"_cert_","")}));
  const misEps = Object.entries(desprendibles||{})
    .filter(([k])=>k.startsWith(emp.id+"_eps_"))
    .map(([k,url])=>({k,url,label:k.replace(emp.id+"_eps_","")}));

  // Convertir archivo a base64 y subir vía API
  const subirDoc = async (file, key) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((res,rej)=>{ reader.onload=()=>res(reader.result); reader.onerror=rej; reader.readAsDataURL(file); });
      await apiFetch('/desprendibles', { method:'POST', body:JSON.stringify({ empId:emp.id, mes:key, url:base64 }) });
      if (refreshDesp) await refreshDesp();
      showToast("Documento cargado ✓");
    } catch(e) { showToast("Error al subir: "+e.message,"error"); }
    finally { setSubiendo(false); }
  };

  const eliminarDoc = async (key) => {
    try {
      await apiFetch('/desprendibles', { method:'POST', body:JSON.stringify({ empId:emp.id, mes:key, url:"" }) });
      if (refreshDesp) await refreshDesp();
      showToast("Documento eliminado","error");
    } catch(e) { showToast("Error","error"); }
  };

  return (
    <div>
      <button className="btn-sec" onClick={onBack} style={{marginBottom:"1.5rem"}}>← Volver</button>
      <div className="detail-header">
        <div className="detail-avatar">{initials(emp.nombre)}</div>
        <div>
          <div className="detail-name">{emp.nombre}</div>
          <div className="detail-meta">{emp.cargo} · CC {emp.cedula}</div>
          <div style={{marginTop:8,display:"flex",gap:6}}>
            <span className={`badge badge-${emp.rol}`}>{ROLES_LABEL[emp.rol]}</span>
            <span className={`badge badge-${emp.estado}`}>{emp.estado==="activo"?"Activo":"Inactivo"}</span>
          </div>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button className="btn-sec" onClick={()=>onReset(emp)} style={{fontSize:11}}>🔑 Resetear contraseña</button>
          <button className="btn-add" onClick={()=>onEdit(emp)}>✏ Editar</button>
        </div>
      </div>
      <div style={{fontSize:9,letterSpacing:"0.25em",textTransform:"uppercase",color:"#999",marginBottom:10}}>Información personal</div>
      <div className="info-grid">
        {[["Teléfono",emp.telefono||"—"],["Email",emp.email||"—"],["Dirección",emp.direccion||"—"],["Fecha nacimiento",fmtDate(emp.fechaNacimiento)],["Fecha ingreso",fmtDate(emp.fechaIngreso)],["Contrato",emp.contrato]].map(([l,v])=>(
          <div key={l} className="info-card"><div className="info-label">{l}</div><div className="info-value">{v}</div></div>
        ))}
      </div>
      <div style={{fontSize:9,letterSpacing:"0.25em",textTransform:"uppercase",color:"#999",margin:"1.5rem 0 10px"}}>Información laboral</div>
      <div className="info-grid">
        {[["Salario mensual",fmt(emp.salario)],["Horas semanales","42h"],["Valor hora",fmt(Math.round(emp.salario/210))],["Usuario sistema",emp.usuario||"—"]].map(([l,v])=>(
          <div key={l} className="info-card"><div className="info-label">{l}</div><div className="info-value">{v}</div></div>
        ))}
      </div>

      {/* ── PANEL DE DOCUMENTOS ── */}
      <div style={{fontSize:9,letterSpacing:"0.25em",textTransform:"uppercase",color:"#999",margin:"2rem 0 10px"}}>Gestión de documentos</div>

      {/* Desprendibles */}
      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.25rem",marginBottom:12}}>
        <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>💰 Desprendibles de pago</div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
          <select className="form-select" value={despMesNum} onChange={e=>setDespMesNum(e.target.value)} style={{width:"auto",minWidth:130}}>
            {meses.map((m,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>)}
          </select>
          <select className="form-select" value={despAnio} onChange={e=>setDespAnio(e.target.value)} style={{width:"auto",minWidth:90}}>
            {["2026","2027","2028","2029","2030"].map(a=><option key={a} value={a}>{a}</option>)}
          </select>
          <label style={{cursor:"pointer"}}>
            <input type="file" accept=".pdf,image/*" style={{display:"none"}} disabled={subiendo} onChange={e=>subirDoc(e.target.files[0], "desp_"+despMes)} />
            <span className="btn-add" style={{fontSize:11,pointerEvents:"none",opacity:subiendo?0.5:1}}>{subiendo?"Subiendo...":"📎 Cargar desprendible"}</span>
          </label>
        </div>
        {misDesp.length===0 ? <div style={{fontSize:12,color:"#999"}}>Sin desprendibles cargados</div>
        : misDesp.map(d=>(
          <div key={d.k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderTop:"1px solid #f0f0f0",fontSize:13}}>
            <span>📄 {d.label}</span>
            <div style={{display:"flex",gap:8}}>
              <a href={d.url} target="_blank" rel="noreferrer" className="btn-sec" style={{fontSize:11,padding:"4px 10px",textDecoration:"none"}}>Ver</a>
              <button className="btn-sec" style={{fontSize:11,padding:"4px 10px",color:"#D32F2F"}} onClick={()=>eliminarDoc("desp_"+d.mes)}>✕</button>
            </div>
          </div>
        ))}
      </div>

      {/* Certificados laborales */}
      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.25rem",marginBottom:12}}>
        <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>📋 Certificados laborales</div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
          <input className="form-input" placeholder="Nombre del certificado" value={certLabel} onChange={e=>setCertLabel(e.target.value)} style={{width:"auto",minWidth:200}} />
          <label style={{cursor:certLabel?"pointer":"not-allowed"}}>
            <input type="file" accept=".pdf,image/*" style={{display:"none"}} disabled={subiendo||!certLabel} onChange={e=>{subirDoc(e.target.files[0], "cert_"+certLabel); setCertLabel("");}} />
            <span className="btn-add" style={{fontSize:11,pointerEvents:"none",opacity:(subiendo||!certLabel)?0.5:1}}>📎 Cargar certificado</span>
          </label>
        </div>
        {misCert.length===0 ? <div style={{fontSize:12,color:"#999"}}>Sin certificados cargados</div>
        : misCert.map(d=>(
          <div key={d.k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderTop:"1px solid #f0f0f0",fontSize:13}}>
            <span>📄 {d.label}</span>
            <div style={{display:"flex",gap:8}}>
              <a href={d.url} target="_blank" rel="noreferrer" className="btn-sec" style={{fontSize:11,padding:"4px 10px",textDecoration:"none"}}>Ver</a>
              <button className="btn-sec" style={{fontSize:11,padding:"4px 10px",color:"#D32F2F"}} onClick={()=>eliminarDoc("cert_"+d.label)}>✕</button>
            </div>
          </div>
        ))}
      </div>

      {/* EPS / Afiliaciones */}
      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.25rem",marginBottom:12}}>
        <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>🏥 EPS / Afiliaciones</div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
          <input className="form-input" placeholder="Nombre del documento" value={epsLabel} onChange={e=>setEpsLabel(e.target.value)} style={{width:"auto",minWidth:200}} />
          <label style={{cursor:epsLabel?"pointer":"not-allowed"}}>
            <input type="file" accept=".pdf,image/*" style={{display:"none"}} disabled={subiendo||!epsLabel} onChange={e=>{subirDoc(e.target.files[0], "eps_"+epsLabel); setEpsLabel("");}} />
            <span className="btn-add" style={{fontSize:11,pointerEvents:"none",opacity:(subiendo||!epsLabel)?0.5:1}}>📎 Cargar documento</span>
          </label>
        </div>
        {misEps.length===0 ? <div style={{fontSize:12,color:"#999"}}>Sin documentos cargados</div>
        : misEps.map(d=>(
          <div key={d.k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderTop:"1px solid #f0f0f0",fontSize:13}}>
            <span>📄 {d.label}</span>
            <div style={{display:"flex",gap:8}}>
              <a href={d.url} target="_blank" rel="noreferrer" className="btn-sec" style={{fontSize:11,padding:"4px 10px",textDecoration:"none"}}>Ver</a>
              <button className="btn-sec" style={{fontSize:11,padding:"4px 10px",color:"#D32F2F"}} onClick={()=>eliminarDoc("eps_"+d.label)}>✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Resumen de minutos del día ─────────────────────────────────────────────────
function ResumenDia({ bloques, fecha }) {
  if (!bloques || bloques.length === 0) return null;
  const d = calcularMinutosDia(bloques, fecha);
  const total = d.totalTrabajado;
  if (total === 0) return null;
  return (
    <div className="resumen-grid">
      <div className="rh-card"><div className="rh-label" style={{color:"#2E7D52"}}>Ordinario diurno</div><div className="rh-value" style={{color:"#2E7D52"}}>{fromMinsHHMM(d.ordinarioDiurno)}</div></div>
      <div className="rh-card"><div className="rh-label">Rec. nocturno +35%</div><div className="rh-value" style={{color:"#1565C0"}}>{fromMinsHHMM(d.ordinarioNocturno)}</div></div>
      <div className="rh-card"><div className="rh-label">Extra diurna +25%</div><div className="rh-value" style={{color:"#E65100"}}>{fromMinsHHMM(d.extraDiurna)}</div></div>
      <div className="rh-card"><div className="rh-label">Extra nocturna +75%</div><div className="rh-value" style={{color:"#D32F2F"}}>{fromMinsHHMM(d.extraNocturna)}</div></div>
      {d.extraFestDiurna>0&&<div className="rh-card"><div className="rh-label">Fest/Dom diurna +100%</div><div className="rh-value" style={{color:"#6A1B9A"}}>{fromMinsHHMM(d.extraFestDiurna)}</div></div>}
      {d.extraFestNocturna>0&&<div className="rh-card"><div className="rh-label">Fest/Dom noct +150%</div><div className="rh-value" style={{color:"#4A148C"}}>{fromMinsHHMM(d.extraFestNocturna)}</div></div>}
      <div className="rh-card" style={{gridColumn:"span 2"}}><div className="rh-label">Total trabajado</div><div className="rh-value">{fromMinsHHMM(total)}</div></div>
    </div>
  );
}

// ── Registro Modal (CEO/RRHH) ─────────────────────────────────────────────────
function RegistroModal({ registro, employees, onSave, onClose }) {
  const today = new Date().toISOString().slice(0,10);
  const blank = { empId:employees[0]?.id||0, fecha:today, bloques:[{tipo:"trabajo",entrada:"",salida:""}], notas:"" };
  const [form, setForm] = useState(registro ? { ...registro } : blank);
  const setField = (k,v) => setForm(f=>({...f,[k]:v}));
  const setBloque = (i,k,v) => setForm(f=>({...f,bloques:f.bloques.map((b,j)=>j===i?{...b,[k]:v}:b)}));
  const addBloque = (tipo) => setForm(f=>({...f,bloques:[...f.bloques,{tipo,entrada:"",salida:""}]}));
  const removeBloque = (i) => setForm(f=>({...f,bloques:f.bloques.filter((_,j)=>j!==i)}));
  const esFest = esFestivo(form.fecha);
  const esDom = esDomingo(form.fecha);
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header"><h2 className="modal-title">{registro?"Editar registro":"Nuevo registro"}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {(esFest||esDom)&&<div className="alert-box">⚠ {esFest?"Festivo nacional":"Domingo"}</div>}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Empleado</label>
              <select className="form-select" value={form.empId} onChange={e=>setField("empId",Number(e.target.value))}>
                {employees.filter(e=>e.estado==="activo").map(e=><option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Fecha</label><input className="form-input" type="date" value={form.fecha} onChange={e=>setField("fecha",e.target.value)} /></div>
          </div>
          <div style={{fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:"#555",margin:"1rem 0 8px"}}>Bloques de tiempo</div>
          {form.bloques.map((b,i)=>(
            <div key={i} className={`bloque-row ${b.tipo}`}>
              <div className={`bloque-tipo ${b.tipo}`}>{b.tipo==="trabajo"?"🟢 Trabajo":"⏸ Descanso"}</div>
              <div><label className="form-label">Entrada</label><input className="form-input" type="time" value={b.entrada} onChange={e=>setBloque(i,"entrada",e.target.value)} /></div>
              <div><label className="form-label">Salida</label><input className="form-input" type="time" value={b.salida} onChange={e=>setBloque(i,"salida",e.target.value)} /></div>
              {form.bloques.length>1&&<button className="btn-icon danger" onClick={()=>removeBloque(i)}>✕</button>}
            </div>
          ))}
          <div style={{display:"flex",gap:8,marginTop:8,marginBottom:"1rem"}}>
            <button className="btn-sec" style={{fontSize:11}} onClick={()=>addBloque("trabajo")}>+ Trabajo</button>
            <button className="btn-sec" style={{fontSize:11}} onClick={()=>addBloque("descanso")}>+ Descanso</button>
          </div>
          <ResumenDia bloques={form.bloques} fecha={form.fecha} />
          <div className="form-group"><label className="form-label">Notas</label><input className="form-input" value={form.notas} onChange={e=>setField("notas",e.target.value)} placeholder="Observaciones..." /></div>
        </div>
        <div className="modal-footer"><button className="btn-sec" onClick={onClose}>Cancelar</button><button className="btn-add" onClick={()=>onSave(form)}>Guardar</button></div>
      </div>
    </div>
  );
}

// ── Aprobación Modal ──────────────────────────────────────────────────────────
function AprobacionModal({ registro, emp, onAction, onClose }) {
  const [notas, setNotas] = useState("");
  const d = calcularMinutosDia(registro.bloques, registro.fecha);
  const vh = emp ? Math.round(emp.salario/210) : 8338;
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header"><h2 className="modal-title">Gestionar horario</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{marginBottom:"1rem"}}>
            <div style={{fontWeight:500,fontSize:14}}>{emp?.nombre}</div>
            <div style={{fontSize:12,color:"#555",marginTop:2}}>{fmtDateShort(registro.fecha)}{esFestivo(registro.fecha)&&" · 🔴 Festivo"}{esDomingo(registro.fecha)&&" · 🟡 Domingo"}</div>
          </div>
          <div style={{marginBottom:"1rem"}}>
            {registro.bloques.map((b,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f0f0f0",fontSize:13}}>
                <span style={{color:b.tipo==="trabajo"?"#111":"#999"}}>{b.tipo==="trabajo"?"🟢 Trabajo":"⏸ Descanso"}</span>
                <span>{b.entrada} – {b.salida}</span>
              </div>
            ))}
          </div>
          <ResumenDia bloques={registro.bloques} fecha={registro.fecha} />
          {d.extraDiurna>0||d.extraNocturna>0 ? (
            <div className="alert-box" style={{marginTop:"1rem"}}>⚠ Este registro contiene horas extra — verifica antes de aprobar</div>
          ) : null}
          <div className="form-group" style={{marginTop:"1rem"}}><label className="form-label">Nota para el empleado (opcional)</label><input className="form-input" value={notas} onChange={e=>setNotas(e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn-danger" onClick={()=>onAction("rechazado",notas)}>✕ Rechazar</button>
          <button className="btn-sec" onClick={onClose}>Cancelar</button>
          <button className="btn-add" onClick={()=>onAction("aprobado",notas)}>✓ Aprobar</button>
        </div>
      </div>
    </div>
  );
}


// ── Barra de Progreso Visual ──────────────────────────────────────────────────
function BarraProgreso({ bloques, fecha, vh }) {
  const DIURNO_INI = 360; const DIURNO_FIN = 1140;
  const fest = esFestivo(fecha)||esDomingo(fecha);
  const sab  = esSabado(fecha);
  const jornadaOrdinaria = sab ? 270 : 450;

  // Colores y etiquetas fijas por tipo de segmento (paleta de diseño)
  const COLOR_MAP = { "ord-d":"#35e69e", "ord-n":"#0f357a", "ext-d":"#1da618", "ext-n":"#ff0000", "fest-d":"#ff00da", "fest-n":"#ff00da", "descanso":"#c7c7c7", "almuerzo":"#c7c7c7", "permiso":"#c7c7c7" };
  const LABEL_MAP = { "ord-d":"Regular", "ord-n":"Recargo Nocturno", "ext-d":"Extra Diurna", "ext-n":"Extra Nocturna", "fest-d":"Extra Festiva", "fest-n":"Extra Festiva", "descanso":"Desayuno", "almuerzo":"Almuerzo", "permiso":"Permiso" };

  const todosBloques = bloques.filter(b=>b.entrada&&b.salida);
  if (todosBloques.length===0) return null;

  // Normalizar a minutos absolutos
  const norm = todosBloques.map(b=>{
    let ini = toMins(b.entrada);
    let fin = toMins(b.salida);
    if (fin<=ini) fin += 1440;
    return { tipo:b.tipo, ini, fin };
  }).sort((a,b)=>a.ini-b.ini);

  const inicioReal = norm[0].ini;
  const finReal = Math.max(...norm.map(b=>b.fin));
  const duracionTotal = finReal - inicioReal;
  if (duracionTotal<=0) return null;

  const pausas = norm.filter(b=>b.tipo!=="trabajo");
  const enPausa = (min) => pausas.some(p=>min>=p.ini&&min<p.fin);

  // Reconstruir bloques de TRABAJO automáticamente: todo lo que no es pausa
  const bloquesTrabajo = [];
  let cursor = inicioReal;
  while (cursor < finReal) {
    if (enPausa(cursor)) { cursor++; continue; }
    let fin = cursor;
    while (fin < finReal && !enPausa(fin)) fin++;
    bloquesTrabajo.push({ _ini:cursor, _fin:fin });
    cursor = fin;
  }
  if (bloquesTrabajo.length===0) return null;

  const segmentos = [];
  let minutosAcumulados = 0;
  bloquesTrabajo.forEach(b=>{
    let cursor = b._ini;
    let restante = b._fin - b._ini;
    while (restante>0) {
      const ordRest = Math.max(0,jornadaOrdinaria-minutosAcumulados);
      const esOrd = ordRest>0&&!fest;
      const cm = cursor%1440;
      const esDiurno = cm>=DIURNO_INI&&cm<DIURNO_FIN;
      let finSeg = cm<DIURNO_INI?Math.min(restante,DIURNO_INI-cm):cm<DIURNO_FIN?Math.min(restante,DIURNO_FIN-cm):Math.min(restante,1440-cm);
      if (esOrd&&finSeg>ordRest) finSeg=ordRest;
      const mins=finSeg;
      let tipo,factor;
      if (fest) {
        if (esDiurno){tipo="fest-d";factor=2.00;}
        else         {tipo="fest-n";factor=2.50;}
      } else if (esOrd) {
        if (esDiurno){tipo="ord-d";factor=null;}
        else         {tipo="ord-n";factor=0.35;}
      } else {
        if (esDiurno){tipo="ext-d";factor=1.25;}
        else         {tipo="ext-n";factor=1.75;}
      }
      segmentos.push({tipo,color:COLOR_MAP[tipo],label:LABEL_MAP[tipo],factor,mins,horaIni:cursor,horaFin:cursor+mins});
      if (!fest&&esOrd) minutosAcumulados+=mins;
      cursor+=mins; restante-=mins;
    }
  });

  // Agregar las pausas (desayuno/almuerzo/permiso) como segmentos visibles más
  pausas.forEach(p=>{
    segmentos.push({tipo:p.tipo, color:COLOR_MAP[p.tipo]||"#c7c7c7", label:LABEL_MAP[p.tipo]||"Descanso", factor:null, mins:p.fin-p.ini, horaIni:p.ini, horaFin:p.fin});
  });
  segmentos.sort((a,b)=>a.horaIni-b.horaIni);
  if (segmentos.length===0) return null;

  // Fusionar consecutivos del mismo tipo
  const merged=[];
  segmentos.forEach(s=>{ const last=merged[merged.length-1]; if(last&&last.tipo===s.tipo){last.mins+=s.mins;last.horaFin=s.horaFin;}else merged.push({...s}); });

  // Calcular posición basada en hora real, no en minutos acumulados de trabajo
  const mp = merged.map(s=>({ ...s, posIni: s.horaIni - inicioReal }));

  const fmtHora=(m)=>{ const mm=Math.round(m)%1440; const h=Math.floor(mm/60); const min=mm%60; return `${h}:${String(min).padStart(2,"0")}`; };
  const fmtDur =(m)=>{ const h=Math.floor(m/60); const min=m%60; return h>0?`${h}h${min>0?" "+min+"m":""}`:min+"m"; };
  const fmtPeso=(n)=>new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(n);
  const textoClaro = (color) => color==="#c7c7c7"; // gris claro necesita texto oscuro para contraste
  const BAR_H = 18;

  return (
    <div style={{marginTop:16,userSelect:"none"}}>

      {/* Etiquetas de hora ENCIMA */}
      <div style={{position:"relative",height:18,marginBottom:3}}>
        {mp.map((s,i)=>{
          const pct=(s.posIni/duracionTotal)*100;
          const txtColor = textoClaro(s.color) ? "#888" : s.color;
          return (
            <span key={i} style={{
              position:"absolute",left:`${pct}%`,
              transform:pct>80?"translateX(-100%)":pct<5?"translateX(0)":"translateX(-50%)",
              fontSize:10,fontWeight:700,color:txtColor,
              whiteSpace:"nowrap",fontVariantNumeric:"tabular-nums",
              textShadow:"0 0 6px rgba(255,255,255,0.8)",
            }}>{fmtHora(s.horaIni)}</span>
          );
        })}
        <span style={{position:"absolute",right:0,fontSize:10,fontWeight:700,
          color:textoClaro(mp[mp.length-1]?.color)?"#888":(mp[mp.length-1]?.color||"#999"),whiteSpace:"nowrap",fontVariantNumeric:"tabular-nums"}}>
          {fmtHora(finReal)}
        </span>
      </div>

      {/* Barra principal — delgada, extremos redondeados, divisores entre cambios de color */}
      <div style={{position:"relative",height:BAR_H,borderRadius:BAR_H/2,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.12)",background:"#f0f0f0"}}>
        {mp.map((s,i)=>(
          <div key={i} style={{
            position:"absolute",
            left:`${(s.posIni/duracionTotal)*100}%`,
            width:`${(s.mins/duracionTotal)*100}%`,
            height:"100%",
            background:s.color,
          }} title={`${s.label} · ${fmtHora(s.horaIni)}–${fmtHora(s.horaFin)}`}/>
        ))}
        {/* Divisores negros solo donde cambia el color */}
        {mp.map((s,i)=>{
          if (i===0) return null;
          if (s.color===mp[i-1].color) return null;
          return <div key={"div"+i} style={{position:"absolute",top:0,left:`${(s.posIni/duracionTotal)*100}%`,width:1.5,height:"100%",background:"#000",transform:"translateX(-50%)"}}/>;
        })}
      </div>

      {/* Leyenda con cálculos: etiqueta · intervalo · duración · valor a cobrar */}
      <div style={{display:"flex",flexDirection:"column",gap:5,marginTop:8}}>
        {mp.map((s,i)=>{
          const valor = s.factor ? fmtPeso(Math.round((s.mins/60)*vh*s.factor)) : "—";
          return (
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:12}}>
              <div style={{width:13,height:13,borderRadius:2,background:s.color,flexShrink:0,marginTop:1,boxShadow:"0 1px 2px rgba(0,0,0,0.15)"}}/>
              <div style={{flex:1}}>
                <span style={{fontWeight:600,color:"#333"}}>{s.label}</span>
                <span style={{color:"#bbb",margin:"0 5px"}}>·</span>
                <span style={{color:"#666",fontVariantNumeric:"tabular-nums"}}>{fmtHora(s.horaIni)} – {fmtHora(s.horaFin)}</span>
                <span style={{color:"#bbb",margin:"0 5px"}}>·</span>
                <span style={{color:"#666"}}>{fmtDur(s.mins)}</span>
                <span style={{color:"#bbb",margin:"0 5px"}}>·</span>
                <strong style={{color:s.factor?s.color:"#999"}}>{valor}</strong>
              </div>
            </div>
          );
        })}
      </div>

      {/* Totales del día */}
      {(()=>{
        const trabajadoMins = mp.filter(x=>!["descanso","almuerzo","permiso"].includes(x.tipo)).reduce((s,x)=>s+x.mins,0);
        const recNoctMins  = mp.filter(x=>x.tipo==="ord-n").reduce((s,x)=>s+x.mins,0);
        const extDiurMins  = mp.filter(x=>x.tipo==="ext-d").reduce((s,x)=>s+x.mins,0);
        const extNoctMins  = mp.filter(x=>x.tipo==="ext-n").reduce((s,x)=>s+x.mins,0);
        const extFestMins  = mp.filter(x=>x.tipo==="fest-d"||x.tipo==="fest-n").reduce((s,x)=>s+x.mins,0);
        const valRecNoct   = Math.round((recNoctMins/60)*vh*0.35);
        const valExtDiur   = Math.round((extDiurMins/60)*vh*1.25);
        const valExtNoct   = Math.round((extNoctMins/60)*vh*1.75);
        const valExtFest   = mp.filter(x=>x.tipo==="fest-d"||x.tipo==="fest-n").reduce((s,x)=>s+Math.round((x.mins/60)*vh*x.factor),0);
        const totalExtras  = valRecNoct+valExtDiur+valExtNoct+valExtFest;
        const hayExtras    = totalExtras>0;
        return (
          <div style={{marginTop:10,paddingTop:10,borderTop:"2px solid #111"}}>
            {hayExtras&&(
              <div style={{display:"flex",flexWrap:"wrap",gap:"6px 16px",marginBottom:8}}>
                {recNoctMins>0&&<span style={{fontSize:12}}>● <span style={{color:"#0f357a"}}>Rec. nocturno</span>: <strong>{fmtDur(recNoctMins)}</strong> = <strong style={{color:"#0f357a"}}>{fmtPeso(valRecNoct)}</strong></span>}
                {extDiurMins>0&&<span style={{fontSize:12}}>● <span style={{color:"#1da618"}}>Extra diurna</span>: <strong>{fmtDur(extDiurMins)}</strong> = <strong style={{color:"#1da618"}}>{fmtPeso(valExtDiur)}</strong></span>}
                {extNoctMins>0&&<span style={{fontSize:12}}>● <span style={{color:"#ff0000"}}>Extra nocturna</span>: <strong>{fmtDur(extNoctMins)}</strong> = <strong style={{color:"#ff0000"}}>{fmtPeso(valExtNoct)}</strong></span>}
                {extFestMins>0&&<span style={{fontSize:12}}>● <span style={{color:"#ff00da"}}>Extra festiva</span>: <strong>{fmtDur(extFestMins)}</strong> = <strong style={{color:"#ff00da"}}>{fmtPeso(valExtFest)}</strong></span>}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <div style={{fontSize:13}}><strong>Total trabajado: {fmtDur(trabajadoMins)}</strong></div>
              {hayExtras&&<div style={{fontSize:13}}><strong>Total extras a pagar: <span style={{color:"#E65100"}}>{fmtPeso(totalExtras)}</span></strong></div>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// Genera lista de meses desde junio 2026 hasta el mes actual (o +2 meses futuros)
function mesesDisponibles() {
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const inicio = new Date(2026, 5, 1); // Junio 2026 (mes 5 = junio)
  const hoy = new Date();
  const fin = new Date(hoy.getFullYear(), hoy.getMonth()+2, 1); // hasta 2 meses adelante
  const lista = [];
  let cur = new Date(inicio);
  while (cur <= fin) {
    const val = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}`;
    lista.push({ value: val, label: `${meses[cur.getMonth()]} ${cur.getFullYear()}` });
    cur.setMonth(cur.getMonth()+1);
  }
  return lista.reverse(); // más reciente primero
}

// ── Horarios Page (CEO/RRHH) ──────────────────────────────────────────────────
function HorariosPage({ registros, setRegistros, employees, isCEO, showToast, refreshData }) {
  const [tab, setTab] = useState("pendientes");
  const [showModal, setShowModal] = useState(false);
  const [editReg, setEditReg] = useState(null);
  const [aprobModal, setAprobModal] = useState(null);
  const [filterEmp, setFilterEmp] = useState("todos");
  const [filterMes, setFilterMes] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  const pendientes = registros.filter(r=>r.estado==="pendiente"&&!r.eliminado);
  const eliminados = registros.filter(r=>r.eliminado);

  const filtrados = registros.filter(r=>{
    if (r.eliminado) return false;
    const matchTab = tab==="todos"||r.estado===tab||(tab==="pendientes"&&r.estado==="pendiente");
    const matchEmp = filterEmp==="todos"||String(r.empId)===filterEmp;
    const matchMes = !filterMes||r.fecha.startsWith(filterMes);
    return matchTab&&matchEmp&&matchMes;
  });

  const handleSave = async (data) => {
    try {
      if (editReg) {
        await apiFetch('/registros/'+editReg.id, { method:'PUT', body:JSON.stringify({...data, bloques:data.bloques, estado:data.estado||'pendiente', aprobadoPor:data.aprobadoPor||null, notas:data.notas||'', notasAprobacion:data.notasAprobacion||'', fecha:data.fecha}) });
        showToast("Registro actualizado");
      } else {
        await apiFetch('/registros', { method:'POST', body:JSON.stringify({empId:data.empId, fecha:data.fecha, bloques:data.bloques, notas:data.notas||''}) });
        showToast("Registro creado");
      }
      await refreshData();
      setShowModal(false); setEditReg(null);
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const handleAprobacion = async (reg, accion, notas) => {
    try {
      await apiFetch('/registros/'+reg.id, { method:'PUT', body:JSON.stringify({...reg, estado:accion, aprobadoPor:"CEO", notasAprobacion:notas}) });
      await refreshData();
      showToast(accion==="aprobado"?"Horario aprobado ✓":"Horario rechazado", accion==="aprobado"?"success":"error");
      setAprobModal(null);
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const getEmp = (id) => employees.find(e=>e.id===id);

  const tabList = [
    ["pendientes","Pendientes"+(pendientes.length>0?" ("+pendientes.length+")":"")],
    ["aprobado","Aprobados"],["rechazado","Rechazados"],["todos","Todos"],
    ...(isCEO ? [["eliminados","🗑 Eliminados"+(eliminados.length>0?" ("+eliminados.length+")":"")]] : []),
  ];

  return (
    <div>
      <div style={{position:"sticky",top:0,zIndex:5,background:"#f8f8f8",paddingBottom:8,marginBottom:8}}>
        {pendientes.length>0&&isCEO&&<div className="alert-box" style={{cursor:"pointer"}} onClick={()=>setTab("pendientes")}>⏳ Tienes <strong>{pendientes.length} registro{pendientes.length>1?"s":""} pendiente{pendientes.length>1?"s":""}</strong> de aprobación</div>}
        <div style={{display:"flex",gap:"1rem",marginBottom:"1rem",flexWrap:"wrap",alignItems:"center"}}>
          <select className="form-select" style={{width:"auto",minWidth:160}} value={filterEmp} onChange={e=>setFilterEmp(e.target.value)}>
            <option value="todos">Todos los empleados</option>
            {employees.filter(e=>e.estado==="activo").map(e=><option key={e.id} value={String(e.id)}>{e.nombre}</option>)}
          </select>
          <select className="form-select" style={{width:"auto",minWidth:160}} value={filterMes} onChange={e=>setFilterMes(e.target.value)}>
            <option value="">Todos los meses</option>
            {mesesDisponibles().map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button className="btn-sec" style={{fontSize:11}} onClick={()=>setFilterMes(new Date().toISOString().slice(0,7))}>Mes actual</button>
          <button className="btn-sec" onClick={()=>setSortAsc(s=>!s)} style={{fontSize:11}}>{sortAsc?"↑ Más viejo primero":"↓ Más nuevo primero"}</button>
          <button className="btn-add" style={{marginLeft:"auto"}} onClick={()=>{setEditReg(null);setShowModal(true);}}>+ Registrar horario</button>
        </div>
        {/* Tabs en desktop, select en móvil */}
        <div className="tabs">{tabList.map(([key,label])=><div key={key} className={`tab ${tab===key?"active":""}`} onClick={()=>setTab(key)}>{label}</div>)}</div>
        <select className="form-select mobile-tab-select" value={tab} onChange={e=>setTab(e.target.value)} style={{marginBottom:0}}>
          {tabList.map(([key,label])=><option key={key} value={key}>{label}</option>)}
        </select>
      </div>

      {tab==="eliminados" ? (
        eliminados.length===0 ? <div className="empty-state"><div className="empty-icon">🗑</div>No hay registros eliminados</div>
        : eliminados.slice().reverse().map(reg=>{
          const emp = getEmp(reg.empId);
          const d = calcularMinutosDia(reg.bloques, reg.fecha);
          return (
            <div key={reg.id} className="horario-card" style={{opacity:0.7,borderLeft:"3px solid #D32F2F"}}>
              <div className="horario-header">
                <div><div className="horario-fecha" style={{textDecoration:"line-through"}}>{fmtDateShort(reg.fecha)}</div><div className="horario-emp">{emp?.nombre} · Eliminado el {reg.eliminadoEn?new Date(reg.eliminadoEn).toLocaleString("es-CO"):"—"}</div></div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn-sec" style={{fontSize:11,padding:"5px 12px"}} onClick={async()=>{
                    try {
                      await apiFetch('/registros/'+reg.id+'/restaurar', {method:'POST'});
                      await refreshData();
                      showToast("Restaurado");
                    } catch(e){ showToast("Error al restaurar","error"); }
                  }}>↩ Restaurar</button>
                  <button className="btn-icon danger" title="Eliminar definitivamente" onClick={async()=>{
                    try {
                      await apiFetch('/registros/'+reg.id+'/definitivo', {method:'DELETE'});
                      await refreshData();
                      showToast("Eliminado definitivamente","error");
                    } catch(e){ showToast("Error al eliminar","error"); }
                  }}>✕</button>
                </div>
              </div>
              <div style={{fontSize:12,color:"#999"}}>Total: {fromMinsHHMM(d.totalTrabajado)} · Estado previo: <span className={"badge badge-"+reg.estado}>{cap(reg.estado)}</span></div>
            </div>
          );
        })
      ) : filtrados.length===0 ? (
        <div className="empty-state"><div className="empty-icon">◷</div>No hay registros en esta vista</div>
      ) : filtrados.slice().sort((a,b)=>sortAsc?a.fecha.localeCompare(b.fecha):b.fecha.localeCompare(a.fecha)).map(reg=>{
        const emp = getEmp(reg.empId);
        const d = calcularMinutosDia(reg.bloques, reg.fecha);
        return (
          <div key={reg.id} className="horario-card">
            <div className="horario-header">
              <div>
                <div className="horario-fecha">{fmtDateShort(reg.fecha)}{esFestivo(reg.fecha)&&" 🔴"}{esDomingo(reg.fecha)&&" 🟡"}</div>
                {filterEmp==="todos"&&<div className="horario-emp">{emp?.nombre||"—"} · {emp?.cargo||""}</div>}
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <span className={`badge badge-${reg.estado}`}>{cap(reg.estado)}</span>
                <button className="btn-sec" style={{padding:"5px 10px",fontSize:11,display:"flex",alignItems:"center",gap:4}} onClick={e=>{e.stopPropagation();setEditReg(reg);setShowModal(true);}}>🖊 Editar</button>
                {isCEO&&reg.estado==="pendiente"&&<button className="btn-add" style={{padding:"5px 14px",fontSize:11}} onClick={e=>{e.stopPropagation();setAprobModal(reg);}}>Gestionar</button>}
                {isCEO&&reg.estado==="aprobado"&&<button className="btn-sec" style={{padding:"5px 12px",fontSize:11}} onClick={e=>{e.stopPropagation();(async()=>{ try { await apiFetch('/registros/'+reg.id,{method:'PUT',body:JSON.stringify({...reg,estado:"pendiente",aprobadoPor:null,notasAprobacion:""})}); await refreshData(); showToast("Devuelto a pendiente"); } catch(e){showToast("Error","error");} })();}}>↩ Desaprobar</button>}
                <button className="btn-icon danger" onClick={e=>{e.stopPropagation();(async()=>{ try { await apiFetch('/registros/'+reg.id,{method:'DELETE'}); await refreshData(); showToast("Movido a eliminados","error"); } catch(e){showToast("Error","error");} })();}}>✕</button>
              </div>
            </div>
            <div className="horario-tags">
              {reg.bloques.map((b,i)=>{
                if(b.tipo==="descanso_dia") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #D4B483",background:"#F5E6C8",color:"#111",fontWeight:500}}>🌙 Descanso</span>;
                if(b.tipo==="no_asistio") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #C0392B",background:"#FADBD8",color:"#922B21",fontWeight:500}}>✗ No asistí</span>;
                if(b.tipo==="incapacidad") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #5B8DD9",background:"#D6E4F7",color:"#1A4A8A",fontWeight:500}}>🏥 Incapacidad</span>;
                return <span key={i} style={{fontSize:11,padding:"2px 8px",borderRadius:1,border:"1px solid #e0e0e0",background:b.tipo==="trabajo"?"#f8f8f8":"#f0f0f0",color:b.tipo==="trabajo"?"#111":"#999"}}>{b.tipo==="trabajo"?"🟢":"⏸"} {b.entrada}–{b.salida}</span>;
              })}
            </div>
            <BarraProgreso bloques={reg.bloques} fecha={reg.fecha} vh={emp?Math.round(emp.salario/210):8338} />
            {(()=>{ const info=infoEnvio(reg.fecha,reg.createdAt); return info&&<div style={{fontSize:11,color:info.color,marginTop:6}}>🕐 {info.label}</div>; })()}
            {reg.notasAprobacion&&<div style={{fontSize:11,color:"#2E7D52",marginTop:6,fontStyle:"italic"}}>"{reg.notasAprobacion}"</div>}
            {reg.notas&&<div style={{fontSize:11,color:"#999",marginTop:4}}>📝 {reg.notas}</div>}
          </div>
        );
      })}

      {/* Resumen mensual por empleado */}
      {tab!=="eliminados"&&filterMes&&(()=>{
        const empIds = [...new Set(filtrados.map(r=>r.empId))];
        if (empIds.length===0) return null;
        return (
          <div style={{marginTop:"2rem",borderTop:"2px solid #111",paddingTop:"1.5rem"}}>
            <div style={{fontSize:12,fontWeight:500,letterSpacing:"0.1em",textTransform:"uppercase",color:"#555",marginBottom:"1rem"}}>Resumen del período — {filterMes}</div>
            {empIds.map(eid=>{
              const emp = getEmp(eid);
              if (!emp) return null;
              const regsEmp = filtrados.filter(r=>r.empId===eid&&r.estado==="aprobado");
              if (regsEmp.length===0) return null;
              const vh = Math.round(emp.salario/210);
              let totMins=0,recNoctMins=0,extDiurMins=0,extNoctMins=0,extFestDMins=0,extFestNMins=0;
              regsEmp.forEach(reg=>{
                const d = calcularMinutosDia(reg.bloques, reg.fecha);
                totMins+=d.totalTrabajado;
                recNoctMins+=d.ordinarioNocturno;
                extDiurMins+=d.extraDiurna;
                extNoctMins+=d.extraNocturna;
                extFestDMins+=d.extraFestDiurna;
                extFestNMins+=d.extraFestNocturna;
              });
              const valRecNoct  = Math.round((recNoctMins/60)*vh*0.35);
              const valExtDiur  = Math.round((extDiurMins/60)*vh*1.25);
              const valExtNoct  = Math.round((extNoctMins/60)*vh*1.75);
              const valExtFestD = Math.round((extFestDMins/60)*vh*2.00);
              const valExtFestN = Math.round((extFestNMins/60)*vh*2.50);
              const totalExtras = valRecNoct+valExtDiur+valExtNoct+valExtFestD+valExtFestN;
              const fmtD = (m)=>{ const h=Math.floor(m/60); const min=m%60; return h>0?`${h}h${min>0?" "+min+"m":""}`:min+"m"; };
              const fmtP = (n)=>new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(n);
              return (
                <div key={eid} style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:2,padding:"1rem 1.5rem",marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:"#111",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",fontWeight:500,flexShrink:0}}>{initials(emp.nombre)}</div>
                    <div><div style={{fontWeight:500,fontSize:14}}>{emp.nombre}</div><div style={{fontSize:11,color:"#999"}}>{regsEmp.length} días aprobados</div></div>
                    <div style={{marginLeft:"auto",textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700}}>Total trabajado: {fmtD(totMins)}</div>
                      {totalExtras>0&&<div style={{fontSize:13,fontWeight:700,color:"#E65100"}}>Total extras: {fmtP(totalExtras)}</div>}
                    </div>
                  </div>
                  {totalExtras>0&&(
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px 16px",paddingTop:8,borderTop:"1px solid #f0f0f0"}}>
                      {recNoctMins>0&&<span style={{fontSize:12}}>🔵 Rec. nocturno: <strong>{fmtD(recNoctMins)}</strong> = <strong style={{color:"#1565C0"}}>{fmtP(valRecNoct)}</strong></span>}
                      {extDiurMins>0&&<span style={{fontSize:12}}>🟡 Extra diurna: <strong>{fmtD(extDiurMins)}</strong> = <strong style={{color:"#F9A825"}}>{fmtP(valExtDiur)}</strong></span>}
                      {extNoctMins>0&&<span style={{fontSize:12}}>🔴 Extra nocturna: <strong>{fmtD(extNoctMins)}</strong> = <strong style={{color:"#E53935"}}>{fmtP(valExtNoct)}</strong></span>}
                      {extFestDMins>0&&<span style={{fontSize:12}}>🩷 Fest diurna: <strong>{fmtD(extFestDMins)}</strong> = <strong style={{color:"#F06292"}}>{fmtP(valExtFestD)}</strong></span>}
                      {extFestNMins>0&&<span style={{fontSize:12}}>💜 Fest nocturna: <strong>{fmtD(extFestNMins)}</strong> = <strong style={{color:"#C2185B"}}>{fmtP(valExtFestN)}</strong></span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {showModal&&<RegistroModal registro={editReg} employees={employees} onSave={handleSave} onClose={()=>{setShowModal(false);setEditReg(null);}} />}
      {aprobModal&&<AprobacionModal registro={aprobModal} emp={getEmp(aprobModal.empId)} onAction={(a,n)=>handleAprobacion(aprobModal,a,n)} onClose={()=>setAprobModal(null)} />}
    </div>
  );
}


// ── HoraInput: hora + minutos + AM/PM ────────────────────────────────────────
function HoraInput({ label, value, onChange }) {
  const parseVal = (v) => {
    if (!v) return { h12: "", mins: "", ampm: "AM" };
    const [h, m] = v.split(":").map(Number);
    return {
      h12: String(h===0?12:h>12?h-12:h),
      mins: String(m).padStart(2,"0"),
      ampm: h>=12?"PM":"AM"
    };
  };

  const { h12, mins, ampm } = parseVal(value);

  // Estado local SOLO para el texto mientras se escribe
  const [draftH, setDraftH] = useState(h12||"");
  const [draftM, setDraftM] = useState(mins||"00");

  // Cuando value cambia desde afuera (ej: copiar hora), sincronizar draft
  useEffect(()=>{
    const p = parseVal(value);
    setDraftH(p.h12||"");
    setDraftM(p.mins||"00");
  }, [value]);

  const emit = (h12str, minsStr, ap) => {
    const h = parseInt(h12str);
    const m = Math.min(59, parseInt(minsStr)||0);
    if (isNaN(h)||h<1||h>12) return;
    const h24 = ap==="AM"?(h===12?0:h):(h===12?12:h+12);
    onChange(`${String(h24).padStart(2,"0")}:${String(m).padStart(2,"0")}`);
  };

  return (
    <div>
      <label className="form-label">{label}</label>
      <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
        <input
          className="form-input"
          value={draftH}
          onChange={e=>setDraftH(e.target.value.replace(/[^0-9]/g,"").slice(0,2))}
          onBlur={e=>emit(e.target.value, draftM, ampm)}
          placeholder="—"
          inputMode="numeric"
          style={{width:52,textAlign:"center",fontSize:17,fontWeight:700,padding:"10px 4px",color:"#111"}}
        />
        <span style={{fontSize:18,color:"#999",fontWeight:300}}>:</span>
        <input
          className="form-input"
          value={draftM}
          onChange={e=>setDraftM(e.target.value.replace(/[^0-9]/g,"").slice(0,2))}
          onBlur={e=>emit(draftH, e.target.value, ampm)}
          placeholder="00"
          inputMode="numeric"
          style={{width:52,textAlign:"center",fontSize:17,fontWeight:700,padding:"10px 4px",color:"#111"}}
        />
        <div style={{display:"flex",flexDirection:"column",gap:3,marginLeft:2}}>
          {["AM","PM"].map(ap=>(
            <button key={ap} onClick={()=>emit(draftH, draftM, ap)} style={{
              padding:"4px 10px",fontSize:11,fontWeight:600,
              border:"1px solid",borderRadius:1,cursor:"pointer",
              fontFamily:"DM Sans,sans-serif",letterSpacing:"0.05em",
              borderColor:ampm===ap?"#111":"#e0e0e0",
              background:ampm===ap?"#111":"#fff",
              color:ampm===ap?"#fff":"#999",
              transition:"all 0.15s",lineHeight:1.2,
            }}>{ap}</button>
          ))}
        </div>
        {value&&<span style={{fontSize:12,color:"#aaa",marginLeft:4,minWidth:40,fontVariantNumeric:"tabular-nums"}}>{value}</span>}
      </div>
    </div>
  );
}

// ── Mi Horario (Empleado) ─────────────────────────────────────────────────────
function MiHorarioPage({ empId, registros, setRegistros, showToast, refreshData }) {
  const today = new Date().toISOString().slice(0,10);
  const [fecha, setFecha] = useState(today);
  const [bloques, setBloques] = useState([{tipo:"trabajo",entrada:"",salida:""}]);
  const [notas, setNotas] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [modoRegistro, setModoRegistro] = useState(null); // null | "normal" | "personalizar"
  const [filterMes, setFilterMes] = useState(new Date().toISOString().slice(0,7));

  const misRegistros = registros.filter(r=>r.empId===empId&&!r.eliminado);
  const [sortEmpAsc, setSortEmpAsc] = useState(true);
  const filtrados = misRegistros.filter(r=>!filterMes||r.fecha.startsWith(filterMes)).slice().sort((a,b)=>sortEmpAsc?a.fecha.localeCompare(b.fecha):b.fecha.localeCompare(a.fecha));
  const yaRegistrado = misRegistros.find(r=>r.fecha===fecha);
  const esFest = esFestivo(fecha);
  const esDom = esDomingo(fecha);
  const esSab = esSabado(fecha);

  const setBloque = (i,k,v) => setBloques(bs=>bs.map((b,j)=>j===i?{...b,[k]:v}:b));
  const addBloque = (tipo) => setBloques(bs=>[...bs,{tipo,entrada:"",salida:""}]);
  const removeBloque = (i) => setBloques(bs=>bs.filter((_,j)=>j!==i));

  const handleEnviar = async () => {
    if (modoRegistro==="normal"||modoRegistro==="personalizar") {
      if (!bloques.find(b=>b.tipo==="trabajo"&&b.entrada&&b.salida)) { showToast("Agrega al menos un bloque de tipo Trabajo con hora de inicio y fin","error"); return; }
    }
    if (!editandoId&&yaRegistrado) { showToast("Ya tienes un registro para este día","error"); return; }
    const bloquesEnviar = modoRegistro==="descanso" ? [{tipo:"descanso_dia",entrada:"",salida:""}]
      : modoRegistro==="noasistio" ? [{tipo:"no_asistio",entrada:"",salida:""}]
      : modoRegistro==="incapacidad" ? [{tipo:"incapacidad",entrada:"",salida:""}]
      : bloques.filter(b=>b.tipo==="trabajo"||b.entrada);
    try {
      if (editandoId) {
        await apiFetch('/registros/'+editandoId, {method:'PUT', body:JSON.stringify({empId,fecha,bloques:bloquesEnviar,estado:"pendiente",aprobadoPor:null,notas,notasAprobacion:""})});
        showToast("Registro actualizado — pendiente de aprobación");
      } else {
        await apiFetch('/registros', {method:'POST', body:JSON.stringify({empId,fecha,bloques:bloquesEnviar,notas})});
        showToast("Enviado para aprobación");
      }
      await refreshData();
      setBloques([{tipo:"trabajo",entrada:"",salida:""}]);
      setNotas(""); setFecha(today); setShowForm(false); setEditandoId(null); setModoRegistro(null);
    } catch(e) { showToast("Error al guardar: "+e.message,"error"); }
  };

  return (
    <div>
      <div className="info-box">ℹ Si no registras tu horario del día, se te liquidará la jornada ordinaria.</div>

      {!showForm ? (
        <div style={{marginBottom:"1.5rem"}}>
          <button className="btn-add" onClick={()=>{setShowForm(true);setModoRegistro(null);setFecha(today);}}>+ Nuevo registro</button>
        </div>
      ) : (
        <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.5rem",marginBottom:"1.5rem",maxWidth:580}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1.25rem"}}>
            <div style={{fontSize:12,fontWeight:500,letterSpacing:"0.1em",textTransform:"uppercase",color:"#555"}}>{editandoId?"Editar registro":"Nuevo registro"}</div>
            <button className="modal-close" onClick={()=>{setShowForm(false);setBloques([{tipo:"trabajo",entrada:"",salida:""}]);setNotas("");setEditandoId(null);setModoRegistro(null);}}>✕</button>
          </div>

          {(esFest||esDom)&&<div className="alert-box">⚠ {esFest?"Festivo":"Domingo"} — tus horas tendrán recargo especial</div>}
          {yaRegistrado&&!editandoId&&<div className="info-box">ℹ Ya tienes un registro para este día: <strong>{yaRegistrado.estado}</strong></div>}

          {/* PASO 1: Solo fecha */}
          <div className="form-group">
            <label className="form-label">Selecciona el día</label>
            <input className="form-input" type="date" value={fecha} onChange={e=>{setFecha(e.target.value);setModoRegistro(null);setBloques([{tipo:"trabajo",entrada:"",salida:""}]);}} style={{maxWidth:220}} />
          </div>

          {/* PASO 2: Elegir modo */}
          {fecha&&!modoRegistro&&(
            <div style={{marginTop:"1.25rem"}}>
              <div style={{fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:"#555",marginBottom:10}}>¿Cómo fue tu jornada?</div>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <button className="btn-add" onClick={()=>{
                  const sab = esSabado(fecha);
                  setBloques(sab ? [{tipo:"trabajo",entrada:"08:00",salida:"10:00"},{tipo:"descanso",entrada:"10:00",salida:"10:15"},{tipo:"trabajo",entrada:"10:15",salida:"12:45"}] : [{tipo:"trabajo",entrada:"08:00",salida:"10:00"},{tipo:"descanso",entrada:"10:00",salida:"10:15"},{tipo:"trabajo",entrada:"10:15",salida:"13:00"},{tipo:"almuerzo",entrada:"13:00",salida:"14:00"},{tipo:"trabajo",entrada:"14:00",salida:"16:45"}]);
                  setModoRegistro("normal");
                }}>✓ Horario normal</button>
                <button className="btn-sec" onClick={()=>{
                  const sab = esSabado(fecha);
                  setBloques(sab ? [{tipo:"trabajo",entrada:"08:00",salida:"10:00"},{tipo:"descanso",entrada:"10:00",salida:"10:15"},{tipo:"trabajo",entrada:"10:15",salida:"12:45"}] : [{tipo:"trabajo",entrada:"08:00",salida:"10:00"},{tipo:"descanso",entrada:"10:00",salida:"10:15"},{tipo:"trabajo",entrada:"10:15",salida:"13:00"},{tipo:"almuerzo",entrada:"13:00",salida:"14:00"},{tipo:"trabajo",entrada:"14:00",salida:"16:45"}]);
                  setModoRegistro("personalizar");
                }}>✏ Personalizar</button>
                <button style={{padding:"8px 18px",fontSize:11,fontWeight:500,border:"1px solid #D4B483",borderRadius:8,cursor:"pointer",background:"#F5E6C8",color:"#111",fontFamily:"Century Gothic,sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}
                  onClick={()=>setModoRegistro("descanso")}>🌙 Descanso</button>
                <button style={{padding:"8px 18px",fontSize:11,fontWeight:500,border:"1px solid #C0392B",borderRadius:8,cursor:"pointer",background:"#FADBD8",color:"#922B21",fontFamily:"Century Gothic,sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}
                  onClick={()=>setModoRegistro("noasistio")}>✗ No asistí</button>
                <button style={{padding:"8px 18px",fontSize:11,fontWeight:500,border:"1px solid #5B8DD9",borderRadius:8,cursor:"pointer",background:"#D6E4F7",color:"#1A4A8A",fontFamily:"Century Gothic,sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}
                  onClick={()=>setModoRegistro("incapacidad")}>🏥 Incapacidad</button>
              </div>
            </div>
          )}

          {/* MODO INCAPACIDAD */}
          {modoRegistro==="incapacidad"&&(
            <div style={{marginTop:"1.25rem",background:"#D6E4F7",border:"1px solid #5B8DD9",borderRadius:10,padding:"1.5rem",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:8}}>🏥</div>
              <div style={{fontWeight:600,fontSize:15,color:"#1A4A8A",marginBottom:4}}>Incapacidad médica</div>
              <div style={{fontSize:12,color:"#555"}}>Este día será registrado como incapacidad y enviado para aprobación.</div>
            </div>
          )}

          {/* MODO DESCANSO */}
          {modoRegistro==="descanso"&&(
            <div style={{marginTop:"1.25rem",background:"#F5E6C8",border:"1px solid #D4B483",borderRadius:10,padding:"1.5rem",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:8}}>🌙</div>
              <div style={{fontWeight:600,fontSize:15,color:"#111",marginBottom:4}}>Día de descanso</div>
              <div style={{fontSize:12,color:"#555"}}>Este día será registrado como descanso y enviado para aprobación.</div>
            </div>
          )}

          {/* MODO NO ASISTIÓ */}
          {modoRegistro==="noasistio"&&(
            <div style={{marginTop:"1.25rem",background:"#FADBD8",border:"1px solid #C0392B",borderRadius:10,padding:"1.5rem",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:8}}>✗</div>
              <div style={{fontWeight:600,fontSize:15,color:"#922B21",marginBottom:4}}>No asistí</div>
              <div style={{fontSize:12,color:"#555"}}>Este día será registrado como ausencia y enviado para aprobación.</div>
            </div>
          )}

          {/* MODO NORMAL */}
          {modoRegistro==="normal"&&(
            <div style={{marginTop:"1.25rem"}}>
              <div style={{background:"#f8f8f8",border:"1px solid #e0e0e0",borderRadius:8,padding:"12px 16px",marginBottom:"1rem"}}>
                <div style={{fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase",color:"#999",marginBottom:8}}>Horario cargado automáticamente</div>
                {bloques.map((b,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:13,borderBottom:"1px solid #f0f0f0",color:b.tipo==="trabajo"?"#111":"#999"}}>
                    <span>{b.tipo==="trabajo"?"🟢 Trabajo":b.tipo==="descanso"?"☕ Desayuno":"🍽 Almuerzo"}</span>
                    <span style={{fontVariantNumeric:"tabular-nums"}}>{b.entrada} – {b.salida}</span>
                  </div>
                ))}
              </div>
              <ResumenDia bloques={bloques.filter(b=>b.tipo==="trabajo")} fecha={fecha} />
              <button className="btn-sec" style={{fontSize:11,marginTop:8}} onClick={()=>setModoRegistro("personalizar")}>✏ Prefiero personalizar</button>
            </div>
          )}

          {/* MODO PERSONALIZAR */}
          {modoRegistro==="personalizar"&&(
            <div style={{marginTop:"1.25rem"}}>
              <div style={{fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:"#555",marginBottom:10}}>Bloques del día</div>
              {bloques.map((b,i)=>(
                <div key={i} style={{background:b.tipo==="trabajo"?"#fff":"#fafafa",border:"1px solid #e0e0e0",borderRadius:8,padding:"12px",marginBottom:8}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:12,alignItems:"start"}}>
                    <HoraInput key={"e"+i+b.entrada} label="Hora de inicio" value={b.entrada} onChange={v=>setBloque(i,"entrada",v)} />
                    <HoraInput key={"s"+i+b.salida} label="Hora de fin" value={b.salida} onChange={v=>setBloque(i,"salida",v)} />
                    {bloques.length>1&&<button className="btn-icon danger" onClick={()=>removeBloque(i)} style={{marginTop:22}}>✕</button>}
                  </div>
                  <div style={{marginTop:10}}>
                    <label className="form-label">Tipo de bloque</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {[["trabajo","🟢 Trabajo"],["descanso","☕ Desayuno"],["almuerzo","🍽 Almuerzo"],["permiso","🕐 Permiso"]].map(([val,label])=>(
                        <button key={val} onClick={()=>setBloque(i,"tipo",val)} style={{padding:"5px 12px",fontSize:12,borderRadius:6,cursor:"pointer",border:"1px solid",borderColor:b.tipo===val?"#111":"#e0e0e0",background:b.tipo===val?"#111":"#fff",color:b.tipo===val?"#fff":"#555",transition:"all 0.15s"}}>{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn-sec" style={{fontSize:11,marginBottom:"1rem"}} onClick={()=>{
                const lastSalida = bloques[bloques.length-1]?.salida||"";
                setBloques(bs=>[...bs,{tipo:"trabajo",entrada:lastSalida,salida:""}]);
              }}>+ Agregar otro bloque</button>
              <ResumenDia bloques={bloques.filter(b=>b.tipo==="trabajo")} fecha={fecha} />
            </div>
          )}

          {/* NOTAS Y ENVIAR */}
          {modoRegistro&&(
            <div style={{marginTop:"1rem"}}>
              <div className="form-group"><label className="form-label">Notas (opcional)</label><input className="form-input" value={notas} onChange={e=>setNotas(e.target.value)} placeholder="Ej: trabajé tarde por pedido urgente..." /></div>
              <div style={{display:"flex",gap:8,marginTop:"1rem"}}>
                <button className="btn-sec" onClick={()=>{setShowForm(false);setBloques([{tipo:"trabajo",entrada:"",salida:""}]);setNotas("");setEditandoId(null);setModoRegistro(null);}}>Cancelar</button>
                <button className="btn-add" onClick={handleEnviar}>Enviar para aprobación →</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="table-header" style={{position:"sticky",top:0,zIndex:5,background:"#f8f8f8",paddingTop:8,paddingBottom:8}}>
        <span className="table-title">Mis registros</span>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button className="btn-sec" onClick={()=>setSortEmpAsc(s=>!s)} style={{fontSize:11}}>{sortEmpAsc?"↑ Más viejo":"↓ Más nuevo"}</button>
          <input className="search-input" type="month" value={filterMes} onChange={e=>setFilterMes(e.target.value)} style={{width:"auto",minWidth:160}} />
          <button className="btn-sec" style={{fontSize:11}} onClick={()=>setFilterMes(new Date().toISOString().slice(0,7))}>Mes actual</button>
        </div>
      </div>

      {filtrados.length===0 ? <div className="empty-state"><div className="empty-icon">◷</div>No tienes registros este período</div>
      : filtrados.map(reg=>{
        const d = calcularMinutosDia(reg.bloques, reg.fecha);
        const esPendiente = reg.estado==="pendiente";
        return (
          <div key={reg.id} className="horario-card">
            <div className="horario-header">
              <div><div className="horario-fecha">{fmtDateShort(reg.fecha)}</div><div className="horario-emp">Jornada registrada</div></div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span className={`badge badge-${reg.estado}`}>{cap(reg.estado)}</span>
                {esPendiente&&<button className="btn-sec" style={{padding:"5px 10px",fontSize:11,display:"flex",alignItems:"center",gap:4}} title="Editar" onClick={()=>{setBloques(reg.bloques.length>0?reg.bloques:[{tipo:"trabajo",entrada:"",salida:""}]);setFecha(reg.fecha);setNotas(reg.notas||"");setEditandoId(reg.id);setShowForm(true);window.scrollTo(0,0);}}>🖊 Editar</button>}
                {esPendiente&&<button className="btn-icon danger" title="Eliminar" onClick={async()=>{ try { await apiFetch('/registros/'+reg.id,{method:'DELETE'}); await refreshData(); showToast("Registro eliminado","error"); } catch(e){showToast("Error","error");} }}>✕</button>}
              </div>
            </div>
            <div className="horario-tags">
              {reg.bloques.map((b,i)=>{
                if(b.tipo==="descanso_dia") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #D4B483",background:"#F5E6C8",color:"#111",fontWeight:500}}>🌙 Descanso</span>;
                if(b.tipo==="no_asistio") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #C0392B",background:"#FADBD8",color:"#922B21",fontWeight:500}}>✗ No asistí</span>;
                if(b.tipo==="incapacidad") return <span key={i} style={{fontSize:11,padding:"4px 12px",borderRadius:8,border:"1px solid #5B8DD9",background:"#D6E4F7",color:"#1A4A8A",fontWeight:500}}>🏥 Incapacidad</span>;
                return <span key={i} style={{fontSize:11,padding:"2px 8px",borderRadius:1,border:"1px solid #e0e0e0",background:b.tipo==="trabajo"?"#f8f8f8":"#f0f0f0",color:b.tipo==="trabajo"?"#111":"#999"}}>{b.tipo==="trabajo"?"🟢":"⏸"} {b.entrada}–{b.salida}</span>;
              })}
            </div>
            <div style={{fontSize:12,color:"#555",marginTop:6,display:"flex",gap:12,flexWrap:"wrap"}}>
              <span>Total: <strong>{fromMinsHHMM(d.totalTrabajado)}</strong></span>
              {d.extraDiurna>0&&<span style={{color:"#E65100"}}>Extra diurna: {fromMinsHHMM(d.extraDiurna)}</span>}
              {d.extraNocturna>0&&<span style={{color:"#D32F2F"}}>Extra noct.: {fromMinsHHMM(d.extraNocturna)}</span>}
              {d.ordinarioNocturno>0&&<span style={{color:"#1565C0"}}>Rec. noct.: {fromMinsHHMM(d.ordinarioNocturno)}</span>}
            </div>
            {reg.notasAprobacion&&<div style={{fontSize:11,color:"#2E7D52",marginTop:4,fontStyle:"italic"}}>CEO: "{reg.notasAprobacion}"</div>}
            {reg.notas&&<div style={{fontSize:11,color:"#999",marginTop:4}}>📝 {reg.notas}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Desprendible ──────────────────────────────────────────────────────────────
function Desprendible({ liq, onClose }) {
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const [anio,mesNum] = liq.mes.split("-");
  const nomMes = meses[parseInt(mesNum)-1]+" "+anio;
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:640}}>
        <div className="modal-header">
          <h2 className="modal-title">Desprendible de pago</h2>
          <div style={{display:"flex",gap:8}}><button className="btn-sec" style={{fontSize:11}} onClick={()=>window.print()}>🖨 Imprimir</button><button className="modal-close" onClick={onClose}>✕</button></div>
        </div>
        <div className="modal-body">
          <div className="desp-wrap">
            <div className="desp-header">
              <div><div className="desp-empresa">Angela Duran</div><div className="desp-title">Grupo Angela Durán SAS</div></div>
              <div className="desp-periodo"><div style={{fontWeight:500}}>Período: {nomMes}</div><div style={{fontSize:11,color:"#999",marginTop:2}}>Desprendible de nómina</div></div>
            </div>
            <div className="desp-emp">
              {[["Empleado",liq.emp.nombre],["Cédula","CC "+liq.emp.cedula],["Cargo",liq.emp.cargo],["Contrato",liq.emp.contrato],["Valor hora","$"+liq.vh.toLocaleString("es-CO")],["Días aprobados",liq.diasAprobados+" días"]].map(([l,v])=>(
                <div key={l} className="desp-field"><span>{l}</span>{v}</div>
              ))}
            </div>
            <table className="desp-items">
              <thead><tr><th>Concepto</th><th style={{textAlign:"right"}}>Tiempo</th><th style={{textAlign:"right"}}>Factor</th><th style={{textAlign:"right"}}>Valor</th></tr></thead>
              <tbody>
                <tr className="section-head"><td colSpan={4}>Devengos</td></tr>
                <tr><td>Salario básico mensual</td><td style={{textAlign:"right"}}>210h</td><td style={{textAlign:"right"}}>×1.00</td><td style={{textAlign:"right"}}>{fmt(liq.emp.salario)}</td></tr>
                {liq.valores.extraDiurna>0&&<tr><td>Horas extra diurnas</td><td style={{textAlign:"right"}}>{fromMinsHHMM(liq.mins.extraDiurna)}</td><td style={{textAlign:"right"}}>+25%</td><td style={{textAlign:"right",color:"#E65100"}}>{fmt(liq.valores.extraDiurna)}</td></tr>}
                {liq.valores.extraNoct>0&&<tr><td>Horas extra nocturnas</td><td style={{textAlign:"right"}}>{fromMinsHHMM(liq.mins.extraNoct)}</td><td style={{textAlign:"right"}}>+75%</td><td style={{textAlign:"right",color:"#D32F2F"}}>{fmt(liq.valores.extraNoct)}</td></tr>}
                {liq.valores.extraFestDiurna>0&&<tr><td>Horas extra festivo/dom. diurnas</td><td style={{textAlign:"right"}}>{fromMinsHHMM(liq.mins.extraFestDiurna)}</td><td style={{textAlign:"right"}}>+100%</td><td style={{textAlign:"right",color:"#6A1B9A"}}>{fmt(liq.valores.extraFestDiurna)}</td></tr>}
                {liq.valores.extraFestNoct>0&&<tr><td>Horas extra festivo/dom. noct.</td><td style={{textAlign:"right"}}>{fromMinsHHMM(liq.mins.extraFestNoct)}</td><td style={{textAlign:"right"}}>+150%</td><td style={{textAlign:"right",color:"#4A148C"}}>{fmt(liq.valores.extraFestNoct)}</td></tr>}
                {liq.valores.recNocturno>0&&<tr><td>Recargo nocturno ordinario</td><td style={{textAlign:"right"}}>{fromMinsHHMM(liq.mins.ordNoct)}</td><td style={{textAlign:"right"}}>+35%</td><td style={{textAlign:"right",color:"#1565C0"}}>{fmt(liq.valores.recNocturno)}</td></tr>}
                <tr><td>Auxilio de transporte</td><td style={{textAlign:"right"}}>—</td><td style={{textAlign:"right"}}>—</td><td style={{textAlign:"right"}}>{fmt(liq.valores.auxTransporte)}</td></tr>
                <tr className="subtotal"><td colSpan={3}>Total devengado</td><td style={{textAlign:"right"}}>{fmt(liq.totales.devengado)}</td></tr>
                <tr className="section-head"><td colSpan={4}>Deducciones</td></tr>
                <tr><td>Salud (4%)</td><td colSpan={2} style={{textAlign:"right"}}>Empleado</td><td style={{textAlign:"right",color:"#D32F2F"}}>− {fmt(liq.descuentos.salud)}</td></tr>
                <tr><td>Pensión (4%)</td><td colSpan={2} style={{textAlign:"right"}}>Empleado</td><td style={{textAlign:"right",color:"#D32F2F"}}>− {fmt(liq.descuentos.pension)}</td></tr>
                {(liq.ibc.ajustadaPiso||liq.ibc.ajustadaTecho)&&<tr><td colSpan={4} style={{fontSize:11,color:"#E67E22",fontStyle:"italic"}}>ℹ IBC ajustado al {liq.ibc.ajustadaPiso?"piso":"techo"} legal ({liq.ibc.ajustadaPiso?"1 SMLMV":"25 SMLMV"}) — base real: {fmt(liq.ibc.baseOriginal)}</td></tr>}
                <tr className="subtotal"><td colSpan={3}>Total deducciones</td><td style={{textAlign:"right",color:"#D32F2F"}}>− {fmt(liq.totales.descuentos)}</td></tr>
                <tr className="neto"><td colSpan={3}>NETO A PAGAR</td><td style={{textAlign:"right"}}>{fmt(liq.totales.neto)}</td></tr>
              </tbody>
            </table>
            <div className="desp-firma">
              <div><div className="firma-line">Firma empleador</div></div>
              <div><div className="firma-line">Firma empleado — {liq.emp.nombre}</div></div>
            </div>
            <div style={{marginTop:"1.5rem",fontSize:10,color:"#999",textAlign:"center",borderTop:"1px solid #e0e0e0",paddingTop:"1rem"}}>Grupo Angela Durán SAS · Generado el {new Date().toLocaleDateString("es-CO")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Nómina Page ───────────────────────────────────────────────────────────────
function NominaPage({ employees, registros, showToast, desprendibles, setDesprendibles }) {
  const mesActual = new Date().toISOString().slice(0,7);
  const [mes, setMes] = useState(mesActual);
  const [empSelec, setEmpSelec] = useState(null);
  const [desprendible, setDesprendible] = useState(null);
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const [anio,mesNum] = mes.split("-");
  const nomMes = meses[parseInt(mesNum)-1]+" "+anio;
  const activos = employees.filter(e=>e.estado==="activo");
  const liquidaciones = activos.map(e=>calcularLiquidacionMes(e,registros,mes));
  const totalNeto = liquidaciones.reduce((s,l)=>s+l.totales.neto,0);
  const liqSelec = empSelec ? liquidaciones.find(l=>l.emp.id===empSelec) : null;

  return (
    <div>
      <div style={{position:"sticky",top:0,zIndex:5,background:"#f8f8f8",paddingBottom:8}}>
        <div style={{display:"flex",gap:"1rem",alignItems:"center",marginBottom:"1rem",flexWrap:"wrap"}}>
          <select className="form-select" value={mes} onChange={e=>setMes(e.target.value)} style={{width:"auto",minWidth:180}}>
            {mesesDisponibles().map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button className="btn-sec" style={{fontSize:11}} onClick={()=>setMes(mesActual)}>Mes actual</button>
          <div style={{marginLeft:"auto",fontSize:12,color:"#999"}}>{liquidaciones.filter(l=>l.diasAprobados>0).length} de {activos.length} con registros</div>
        </div>
      </div>
      <div className="nom-grid" style={{gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))"}}>
        {[{label:"Total nómina neta",value:fmt(totalNeto),nc:"#2E7D52"},{label:"Total horas extra",value:fmt(liquidaciones.reduce((s,l)=>s+l.valores.extraDiurna+l.valores.extraNoct,0)),nc:"#E65100"},{label:"Total recargos nocturnos",value:fmt(liquidaciones.reduce((s,l)=>s+l.valores.recNocturno,0)),nc:"#1565C0"}].map(c=>(
          <div key={c.label} className="nom-card" style={{"--nc":c.nc}}><div className="nom-label">{c.label}</div><div className="nom-value">{c.value}</div></div>
        ))}
      </div>
      <table className="liq-table">
        <thead><tr><th>Empleado</th><th>Días</th><th>Extra diurna</th><th>Extra noct.</th><th>Rec. noct.</th><th>Devengado</th><th>Desc.</th><th style={{textAlign:"right"}}>Neto</th><th></th></tr></thead>
        <tbody>
          {liquidaciones.map(l=>(
            <tr key={l.emp.id} style={{cursor:"pointer"}} onClick={()=>setEmpSelec(empSelec===l.emp.id?null:l.emp.id)}>
              <td><div className="emp-name-cell"><div className="emp-avatar">{initials(l.emp.nombre)}</div><div><div className="emp-name">{l.emp.nombre}</div><div className="emp-id">{l.emp.cargo}</div></div></div></td>
              <td><span style={{color:"#999",fontSize:11}}>Días: </span>{l.diasAprobados||"—"}</td>
              <td>{l.valores.extraDiurna>0?<span style={{color:"#E65100"}}><span style={{color:"#999",fontSize:11}}>Extra: </span>{fmt(l.valores.extraDiurna)}</span>:<span style={{color:"#ccc"}}>—</span>}</td>
              <td>{l.valores.extraNoct>0?<span style={{color:"#D32F2F"}}><span style={{color:"#999",fontSize:11}}>Extra noct: </span>{fmt(l.valores.extraNoct)}</span>:<span style={{color:"#ccc"}}>—</span>}</td>
              <td>{l.valores.recNocturno>0?<span style={{color:"#1565C0"}}><span style={{color:"#999",fontSize:11}}>Rec.noct: </span>{fmt(l.valores.recNocturno)}</span>:<span style={{color:"#ccc"}}>—</span>}</td>
              <td><span style={{color:"#999",fontSize:11}}>Devengado: </span><span style={{fontWeight:500}}>{fmt(l.totales.devengado)}</span></td>
              <td><span style={{color:"#999",fontSize:11}}>Descuentos: </span><span style={{color:"#D32F2F"}}>−{fmt(l.totales.descuentos)}</span></td>
              <td><span style={{color:"#999",fontSize:11}}>Neto: </span><span style={{textAlign:"right",fontWeight:500,color:"#2E7D52",fontSize:14}}>{fmt(l.totales.neto)}</span></td>
              <td>
                <div style={{display:"flex",gap:6}}>
                  {desprendibles[l.emp.id+"_desp_"+mes] ? (
                    <span className="badge badge-aprobado">PDF ✓</span>
                  ) : (
                    <label style={{cursor:"pointer"}}>
                      <input type="file" accept=".pdf" style={{display:"none"}} onChange={e=>{ const f=e.target.files[0]; if(f){const url=URL.createObjectURL(f);setDesprendibles(prev=>({...prev,[l.emp.id+"_desp_"+mes]:url}));showToast("PDF cargado — "+l.emp.nombre.split(" ")[0]+" ya puede verlo");}}} />
                      <span className="btn-sec" style={{fontSize:10,padding:"4px 10px",pointerEvents:"none"}}>📎 PDF</span>
                    </label>
                  )}
                  <button className="btn-sec" style={{fontSize:10,padding:"4px 10px"}} onClick={e=>{e.stopPropagation();setDesprendible(l);}}>Ver</button>
                </div>
              </td>
            </tr>
          ))}
          <tr className="total-row">
            <td colSpan={5}>TOTAL {nomMes}</td>
            <td>{fmt(liquidaciones.reduce((s,l)=>s+l.totales.devengado,0))}</td>
            <td style={{color:"#D32F2F"}}>−{fmt(liquidaciones.reduce((s,l)=>s+l.totales.descuentos,0))}</td>
            <td style={{textAlign:"right",color:"#2E7D52"}}>{fmt(totalNeto)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {liqSelec&&(
        <div style={{marginTop:"1rem",background:"#fff",border:"1px solid #e0e0e0",borderRadius:2,padding:"1.5rem"}}>
          <div style={{fontSize:13,fontWeight:500,marginBottom:"1rem"}}>Detalle día a día — {liqSelec.emp.nombre}</div>
          {liqSelec.detalleDias.length===0 ? <div style={{color:"#999",fontSize:13}}>Sin registros aprobados.</div>
          : liqSelec.detalleDias.map(d=>{
            const fmtP = (n)=>new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(n);
            const fmtM = (m)=>{ const h=Math.floor(m/60); const min=m%60; return h>0?`${h}h${min>0?" "+min+"m":""}`:min+"m"; };
            if (d.tipo==="descanso") return (
              <div key={d.fecha} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 10px",borderBottom:"1px solid #f0f0f0",fontSize:13,background:"#FEFAF3",borderRadius:4,marginBottom:2}}>
                <span style={{fontWeight:600,minWidth:180}}>{fmtDateShort(d.fecha)}</span>
                <span style={{background:"#F5E6C8",border:"1px solid #D4B483",borderRadius:6,padding:"2px 10px",fontSize:12}}>🌙 Descanso</span>
              </div>
            );
            if (d.tipo==="inasistencia") return (
              <div key={d.fecha} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 10px",borderBottom:"1px solid #f0f0f0",fontSize:13,background:"#FFF5F5",borderRadius:4,marginBottom:2}}>
                <span style={{fontWeight:600,minWidth:180}}>{fmtDateShort(d.fecha)}</span>
                <span style={{background:"#FADBD8",border:"1px solid #C0392B",borderRadius:6,padding:"2px 10px",fontSize:12,color:"#922B21"}}>✗ No asistió</span>
                <span style={{marginLeft:"auto",color:"#D32F2F",fontWeight:600}}>− {fmtP(d.descValor)}</span>
              </div>
            );
            if (d.tipo==="incapacidad") return (
              <div key={d.fecha} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 10px",borderBottom:"1px solid #f0f0f0",fontSize:13,background:"#F0F5FF",borderRadius:4,marginBottom:2}}>
                <span style={{fontWeight:600,minWidth:180}}>{fmtDateShort(d.fecha)}</span>
                <span style={{background:"#D6E4F7",border:"1px solid #5B8DD9",borderRadius:6,padding:"2px 10px",fontSize:12,color:"#1A4A8A"}}>🏥 Incapacidad día {d.diaNum}</span>
                <span style={{fontSize:11,color:"#555"}}>{d.esEmpleador?"Empleador 66.67%":"EPS 66.67%"}</span>
                {d.esEmpleador&&<span style={{marginLeft:"auto",color:"#1A4A8A",fontWeight:600}}>− {fmtP(d.valorIncap)}</span>}
                {!d.esEmpleador&&<span style={{marginLeft:"auto",color:"#2E7D52",fontSize:11}}>Paga EPS</span>}
              </div>
            );
            // Día trabajado
            const hayNovedad = d.extraDiurna>0||d.extraNocturna>0||d.extraFestDiurna>0||d.extraFestNocturna>0||d.ordinarioNocturno>0||d.faltanteMin>0;
            return (
              <div key={d.fecha} style={{padding:"8px 10px",borderBottom:"1px solid #f0f0f0",marginBottom:2,borderRadius:4,background:hayNovedad?"#FAFFFE":"#fff"}}>
                <div style={{display:"flex",alignItems:"center",gap:12,fontSize:13,flexWrap:"wrap"}}>
                  <span style={{fontWeight:600,minWidth:180}}>{fmtDateShort(d.fecha)}</span>
                  <span style={{color:"#555"}}>Ordinario: {fromMinsHHMM(d.ordinarioDiurno)}</span>
                  {d.ordinarioNocturno>0&&<span style={{color:"#1565C0"}}>+ Rec.noct: {fromMinsHHMM(d.ordinarioNocturno)} · {fmtP(Math.round((d.ordinarioNocturno/60)*liqSelec.vh*0.35))}</span>}
                  {d.extraDiurna>0&&<span style={{color:"#E65100"}}>+ Extra diurna: {fromMinsHHMM(d.extraDiurna)} · {fmtP(Math.round((d.extraDiurna/60)*liqSelec.vh*1.25))}</span>}
                  {d.extraNocturna>0&&<span style={{color:"#D32F2F"}}>+ Extra noct: {fromMinsHHMM(d.extraNocturna)} · {fmtP(Math.round((d.extraNocturna/60)*liqSelec.vh*1.75))}</span>}
                  {d.extraFestDiurna>0&&<span style={{color:"#9C27B0"}}>+ Fest diurna: {fromMinsHHMM(d.extraFestDiurna)} · {fmtP(Math.round((d.extraFestDiurna/60)*liqSelec.vh*2))}</span>}
                  {d.extraFestNocturna>0&&<span style={{color:"#C2185B"}}>+ Fest noct: {fromMinsHHMM(d.extraFestNocturna)} · {fmtP(Math.round((d.extraFestNocturna/60)*liqSelec.vh*2.5))}</span>}
                  {d.faltanteMin>0&&<span style={{color:"#D32F2F",marginLeft:"auto"}}>⏰ Faltaron {fmtM(d.faltanteMin)} · − {fmtP(d.faltanteVal)}</span>}
                </div>
              </div>
            );
          })}
          {/* Totales del mes */}
          {(()=>{
            const fmtP = (n)=>new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(n);
            const fmtM = (m)=>{ const h=Math.floor(m/60); const min=m%60; return h>0?`${h}h${min>0?" "+min+"m":""}`:min+"m"; };
            const {mins,valores,descuentosLaborales,compensacion} = liqSelec;
            const hayExtras = valores.extraDiurna+valores.extraNoct+valores.extraFestDiurna+valores.extraFestNoct+valores.recNocturno > 0;
            const hayDescs = descuentosLaborales.total > 0;
            return (
              <div style={{marginTop:"1rem",paddingTop:"1rem",borderTop:"2px solid #111"}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:8}}>
                  {valores.recNocturno>0&&<div style={{background:"#EBF3FF",border:"1px solid #90CAF9",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#1565C0",marginBottom:4}}>Recargo nocturno</div><div style={{fontWeight:600,color:"#1565C0"}}>{fmtM(mins.ordNoct)} · + {fmtP(valores.recNocturno)}</div></div>}
                  {valores.extraDiurna>0&&<div style={{background:"#FFF8E1",border:"1px solid #FFC107",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#E65100",marginBottom:4}}>Extra diurna +25%</div><div style={{fontWeight:600,color:"#E65100"}}>{fmtM(mins.extraDiurna)} · + {fmtP(valores.extraDiurna)}</div></div>}
                  {valores.extraNoct>0&&<div style={{background:"#FFEBEE",border:"1px solid #E53935",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#D32F2F",marginBottom:4}}>Extra nocturna +75%</div><div style={{fontWeight:600,color:"#D32F2F"}}>{fmtM(mins.extraNoct)} · + {fmtP(valores.extraNoct)}</div></div>}
                  {valores.extraFestDiurna>0&&<div style={{background:"#F3E5F5",border:"1px solid #CE93D8",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#9C27B0",marginBottom:4}}>Fest/Dom diurna +100%</div><div style={{fontWeight:600,color:"#9C27B0"}}>{fmtM(mins.extraFestDiurna)} · + {fmtP(valores.extraFestDiurna)}</div></div>}
                  {valores.extraFestNoct>0&&<div style={{background:"#FCE4EC",border:"1px solid #F48FB1",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#C2185B",marginBottom:4}}>Fest/Dom nocturna +150%</div><div style={{fontWeight:600,color:"#C2185B"}}>{fmtM(mins.extraFestNoct)} · + {fmtP(valores.extraFestNoct)}</div></div>}
                  {descuentosLaborales.diasDesc>0&&<div style={{background:"#FFF5F5",border:"1px solid #C0392B",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#922B21",marginBottom:4}}>Inasistencias</div><div style={{fontWeight:600,color:"#922B21"}}>{descuentosLaborales.diasDesc} día(s) · − {fmtP(descuentosLaborales.inasistencias)}</div></div>}
                  {compensacion.minutosCompensados>0&&<div style={{background:"#F1F8F4",border:"1px solid #66BB6A",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#2E7D52",marginBottom:4}}>Compensado con extras</div><div style={{fontWeight:600,color:"#2E7D52"}}>{fmtM(compensacion.minutosCompensados)} · no se paga ni se descuenta</div></div>}
                  {descuentosLaborales.minsDesc>0&&<div style={{background:"#FFF5F5",border:"1px solid #E67E22",borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#E67E22",marginBottom:4}}>Tiempo faltante{compensacion.minutosCompensados>0?" (sin cubrir)":""}</div><div style={{fontWeight:600,color:"#E67E22"}}>{fmtM(descuentosLaborales.minsDesc)} · − {fmtP(descuentosLaborales.minutos)}</div></div>}
                </div>
              </div>
            );
          })()}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:8,marginTop:"1rem"}}>
            {[["Salario base",fmt(liqSelec.emp.salario),"#111"],["Extra diurna ×1.25",fmt(liqSelec.valores.extraDiurna),"#E65100"],["Extra noct. ×1.75",fmt(liqSelec.valores.extraNoct),"#D32F2F"],["Rec. noct. +35%",fmt(liqSelec.valores.recNocturno),"#1565C0"],["Aux. transporte",fmt(liqSelec.valores.auxTransporte),"#555"],["Salud −4%","−"+fmt(liqSelec.descuentos.salud),"#D32F2F"],["Pensión −4%","−"+fmt(liqSelec.descuentos.pension),"#D32F2F"],["NETO A PAGAR",fmt(liqSelec.totales.neto),"#2E7D52"]].map(([l,v,c])=>(
              <div key={l} style={{background:"#f8f8f8",border:"1px solid #e0e0e0",borderRadius:1,padding:"8px 12px"}}>
                <div style={{fontSize:9,letterSpacing:"0.15em",textTransform:"uppercase",color:"#999",marginBottom:4}}>{l}</div>
                <div style={{fontSize:14,fontWeight:500,color:c}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {desprendible&&<Desprendible liq={desprendible} onClose={()=>setDesprendible(null)} />}
    </div>
  );
}

// ── Mis Documentos ────────────────────────────────────────────────────────────
function MisDocumentosPage({ empId, desprendibles }) {
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const [tab, setTab] = useState("desprendibles");
  const [viendoPdf, setViendoPdf] = useState(null);

  const misDesp = Object.entries(desprendibles)
    .filter(([key])=>key.startsWith(empId+"_desp_"))
    .map(([key,url])=>{ const mes=key.replace(empId+"_desp_",""); const [anio,mesNum]=mes.split("-"); return {key,mes,url,label:meses[parseInt(mesNum)-1]+" "+anio}; })
    .sort((a,b)=>b.mes.localeCompare(a.mes));

  const certLaborales = Object.entries(desprendibles)
    .filter(([key])=>key.startsWith(empId+"_cert_"))
    .map(([key,url])=>({ key, url, label: key.replace(empId+"_cert_","") }));

  const docsEps = Object.entries(desprendibles)
    .filter(([key])=>key.startsWith(empId+"_eps_"))
    .map(([key,url])=>({ key, url, label: key.replace(empId+"_eps_","") }));

  const DocCard = ({label, url, sub}) => (
    <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:10,padding:"1rem 1.25rem",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div><div style={{fontWeight:500,fontSize:13}}>📄 {label}</div><div style={{fontSize:11,color:"#999",marginTop:2}}>{sub}</div></div>
      <div style={{display:"flex",gap:8}}>
        <button className="btn-sec" style={{fontSize:11,padding:"5px 12px"}} onClick={()=>setViendoPdf({url,label})}>Ver</button>
        <a href={url} download className="btn-add" style={{fontSize:11,padding:"5px 12px",textDecoration:"none"}}>⬇</a>
      </div>
    </div>
  );

  const tabs = [["desprendibles","💰 Desprendibles"],["certificados","📋 Certificados"],["eps","🏥 EPS / Afiliaciones"]];

  return (
    <div>
      <div className="tabs">
        {tabs.map(([key,label])=><div key={key} className={`tab ${tab===key?"active":""}`} onClick={()=>setTab(key)}>{label}</div>)}
      </div>
      <select className="form-select mobile-tab-select" value={tab} onChange={e=>setTab(e.target.value)} style={{marginBottom:"1rem"}}>
        {tabs.map(([key,label])=><option key={key} value={key}>{label}</option>)}
      </select>

      {tab==="desprendibles"&&(
        misDesp.length===0
          ? <div className="empty-state"><div className="empty-icon">💰</div><div>No tienes desprendibles aún</div><div style={{fontSize:11,color:"#999",marginTop:8}}>Tu empleador los cargará aquí cada mes</div></div>
          : misDesp.map(d=><DocCard key={d.key} label={d.label} url={d.url} sub="Comprobante de pago" />)
      )}
      {tab==="certificados"&&(
        certLaborales.length===0
          ? <div className="empty-state"><div className="empty-icon">📋</div><div>No tienes certificados laborales</div><div style={{fontSize:11,color:"#999",marginTop:8}}>Tu empleador los cargará cuando los solicites</div></div>
          : certLaborales.map(d=><DocCard key={d.key} label={d.label} url={d.url} sub="Certificado laboral" />)
      )}
      {tab==="eps"&&(
        docsEps.length===0
          ? <div className="empty-state"><div className="empty-icon">🏥</div><div>No tienes documentos de EPS</div><div style={{fontSize:11,color:"#999",marginTop:8}}>Tu empleador los cargará aquí</div></div>
          : docsEps.map(d=><DocCard key={d.key} label={d.label} url={d.url} sub="EPS / Afiliación" />)
      )}

      {viendoPdf&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setViendoPdf(null)}>
          <div className="modal" style={{maxWidth:720,height:"90vh",display:"flex",flexDirection:"column"}}>
            <div className="modal-header"><h2 className="modal-title">{viendoPdf.label}</h2><div style={{display:"flex",gap:8}}><a href={viendoPdf.url} download className="btn-sec" style={{fontSize:11,padding:"6px 14px",textDecoration:"none"}}>⬇</a><button className="modal-close" onClick={()=>setViendoPdf(null)}>✕</button></div></div>
            <div style={{flex:1,padding:"1rem",overflow:"hidden"}}><iframe src={viendoPdf.url} style={{width:"100%",height:"100%",border:"1px solid #e0e0e0"}} title={viendoPdf.label} /></div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── ChangePassForm ────────────────────────────────────────────────────────────
function ChangePassForm({ onSave, onClose }) {
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const handleSave = () => {
    if (newPass.length < 4) { setError("Mínimo 4 caracteres"); return; }
    if (newPass !== confirm) { setError("Las contraseñas no coinciden"); return; }
    onSave(newPass);
  };
  return (
    <div>
      {error&&<div className="login-error">{error}</div>}
      <div className="form-group"><label className="form-label">Nueva contraseña</label><input className="form-input" type="password" value={newPass} onChange={e=>{setNewPass(e.target.value);setError("");}} placeholder="Mínimo 4 caracteres" autoFocus /></div>
      <div className="form-group"><label className="form-label">Confirmar contraseña</label><input className="form-input" type="password" value={confirm} onChange={e=>{setConfirm(e.target.value);setError("");}} placeholder="Repite la contraseña" onKeyDown={e=>e.key==="Enter"&&handleSave()} /></div>
      <div style={{display:"flex",gap:8,marginTop:"1rem"}}>
        <button className="btn-sec" onClick={onClose}>Cancelar</button>
        <button className="btn-add" onClick={handleSave}>Guardar contraseña</button>
      </div>
    </div>
  );
}


// ── Mi Perfil (Empleado) ──────────────────────────────────────────────────────
function MiPerfilPage({ currentUser, employees, onUpdate, showToast }) {
  const emp = employees.find(e=>e.id===currentUser.empId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ email:"", telefono:"", direccion:"" });
  const [foto, setFoto] = useState(null);

  useEffect(()=>{
    if(emp){
      setForm({ email:emp.email||"", telefono:emp.telefono||"", direccion:emp.direccion||"" });
      setFoto(emp.foto||null);
    }
  }, [emp]);

  if (!emp) return <div className="empty-state">No se encontró tu perfil</div>;

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Comprimir y redimensionar a máximo 300x300px
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 300;
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h = Math.round(h*MAX/w); w = MAX; } }
        else { if (h > MAX) { w = Math.round(w*MAX/h); h = MAX; } }
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.8);
        setFoto(compressed);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      await onUpdate({ ...emp, ...form, foto });
      setEditing(false);
      showToast("Perfil actualizado ✓");
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const initials = emp.nombre.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

  return (
    <div style={{maxWidth:520}}>
      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"2rem",marginBottom:"1rem",textAlign:"center"}}>
        {/* Avatar con opción de cambiar foto */}
        <div style={{position:"relative",width:90,height:90,margin:"0 auto 1rem"}}>
          {foto
            ? <img src={foto} alt="Foto perfil" style={{width:90,height:90,borderRadius:"50%",objectFit:"cover",border:"3px solid #e0e0e0"}} />
            : <div style={{width:90,height:90,borderRadius:"50%",background:"#111",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,color:"#fff",fontWeight:500}}>{initials}</div>
          }
          <label style={{position:"absolute",bottom:0,right:0,background:"#111",color:"#fff",borderRadius:"50%",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14,border:"2px solid #fff"}} title="Cambiar foto">
            📷
            <input type="file" accept="image/*" style={{display:"none"}} onChange={handleFoto} />
          </label>
        </div>
        <div style={{fontSize:20,fontWeight:600,color:"#111"}}>{emp.nombre}</div>
        <div style={{fontSize:13,color:"#555",marginTop:4}}>{emp.cargo}</div>
        <div style={{marginTop:8,display:"flex",gap:6,justifyContent:"center"}}>
          <span className="badge badge-activo">{emp.estado==="activo"?"Activo":"Inactivo"}</span>
          <span className="badge badge-empleado">{emp.contrato}</span>
        </div>
        {foto&&<button className="btn-sec" style={{fontSize:11,marginTop:"1rem"}} onClick={()=>setFoto(null)}>✕ Quitar foto</button>}
        {foto&&!editing&&<button className="btn-add" style={{fontSize:11,marginTop:"1rem",marginLeft:8}} onClick={handleSave}>Guardar foto</button>}
      </div>

      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.5rem",marginBottom:"1rem"}}>
        <div style={{fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:"#999",marginBottom:"1rem"}}>Información laboral</div>
        {[["Cédula","CC "+emp.cedula],["Fecha de ingreso",emp.fechaIngreso?new Date(emp.fechaIngreso+"T12:00:00").toLocaleDateString("es-CO",{day:"2-digit",month:"long",year:"numeric"}):"—"],["Salario",new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(emp.salario)]].map(([l,v])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f0f0f0",fontSize:13}}>
            <span style={{color:"#999"}}>{l}</span><span style={{fontWeight:500}}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"1.5rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
          <div style={{fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:"#999"}}>Información de contacto</div>
          {!editing&&<button className="btn-sec" style={{fontSize:11}} onClick={()=>setEditing(true)}>✏ Editar</button>}
        </div>
        {editing ? (
          <>
            <div className="form-group"><label className="form-label">Correo electrónico</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Teléfono / Celular</label><input className="form-input" value={form.telefono} onChange={e=>setForm(f=>({...f,telefono:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Dirección</label><input className="form-input" value={form.direccion} onChange={e=>setForm(f=>({...f,direccion:e.target.value}))} /></div>
            <div style={{display:"flex",gap:8,marginTop:"1rem"}}>
              <button className="btn-sec" onClick={()=>setEditing(false)}>Cancelar</button>
              <button className="btn-add" onClick={handleSave}>Guardar cambios</button>
            </div>
          </>
        ) : (
          [["Correo",emp.email||"—"],["Teléfono",emp.telefono||"—"],["Dirección",emp.direccion||"—"]].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f0f0f0",fontSize:13}}>
              <span style={{color:"#999"}}>{l}</span><span style={{fontWeight:500}}>{v}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [authed, setAuthed] = useState(false);
  const [loginForm, setLoginForm] = useState({user:"",pass:""});
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [employees, setEmployees] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [desprendibles, setDesprendibles] = useState({});
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [resetEmp, setResetEmp] = useState(null);
  const [mustChangePass, setMustChangePass] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [detailEmp, setDetailEmp] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [toast, setToast] = useState(null);
  const [confirmDeleteEmp, setConfirmDeleteEmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showChangePassCEO, setShowChangePassCEO] = useState(false);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  // Cargar datos al iniciar sesión
  const loadData = async () => {
    try {
      setLoading(true);
      const [emps, regs, desps] = await Promise.all([
        apiFetch('/empleados'),
        apiFetch('/registros'),
        apiFetch('/desprendibles'),
      ]);
      setEmployees(emps);
      setRegistros(regs);
      setDesprendibles(desps);
    } catch(e) {
      showToast("Error cargando datos: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const data = await apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify({ usuario: loginForm.user, password: loginForm.pass })
      });
      localStorage.setItem('token', data.token);
      setCurrentUser(data.user);
      setAuthed(true);
      if (data.user.mustChangePassword) {
        setMustChangePass(true);
      } else {
        await loadData();
        setPage(data.user.rol==="empleado"?"mi-horario":"dashboard");
      }
    } catch(e) {
      setLoginError(e.message || "Usuario o contraseña incorrectos");
      setTimeout(()=>setLoginError(""),3000);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleReset = async (emp, newPass) => {
    try {
      await apiFetch('/empleados/'+emp.id+'/reset-password', { method:'POST', body:JSON.stringify({newPassword:newPass}) });
      setEmployees(es=>es.map(e=>e.id===emp.id?{...e,mustChangePassword:true}:e));
      setResetEmp(null);
      showToast("Contraseña reseteada — "+emp.nombre.split(" ")[0]+" deberá cambiarla al iniciar sesión");
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const handleChangePassword = async (newPass) => {
    try {
      await apiFetch('/change-password', { method:'POST', body:JSON.stringify({newPassword:newPass}) });
      setMustChangePass(false);
      await loadData();
      setPage(currentUser.rol==="empleado"?"mi-horario":"dashboard");
      showToast("Contraseña actualizada correctamente ✓");
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const handleSaveEmployee = async (data) => {
    try {
      if (editEmp) {
        await apiFetch('/empleados/'+editEmp.id, { method:'PUT', body:JSON.stringify(data) });
        showToast("Empleado actualizado");
      } else {
        await apiFetch('/empleados', { method:'POST', body:JSON.stringify(data) });
        showToast("Empleado creado");
      }
      await loadData();
      setShowEmpModal(false); setEditEmp(null);
    } catch(e) { showToast("Error: "+e.message,"error"); }
  };

  const empleadosVisibles = employees.filter(e=>e.rol!=="ceo");
  const activos = empleadosVisibles.filter(e=>e.estado==="activo").length;
  const pendientesCount = registros.filter(r=>r.estado==="pendiente").length;
  const isEmpleado = currentUser?.rol==="empleado";
  const isCEO = currentUser?.rol==="ceo";
  const ROLES_LABEL = { ceo:"CEO", rrhh:"RRHH", empleado:"Empleado" };

  const navItems = isEmpleado ? [
    {id:"mi-horario",icon:"◷",label:"Mis horarios"},
    {id:"mis-documentos",icon:"◎",label:"Mis documentos"},
    {id:"mi-perfil",icon:"◉",label:"Mi perfil"},
  ] : [
    {id:"dashboard",icon:"◈",label:"Dashboard"},
    {id:"empleados",icon:"◉",label:"Empleados"},
    {id:"horarios",icon:"◷",label:"Horarios",badge:pendientesCount>0?pendientesCount:null},
    {id:"nomina",icon:"◎",label:"Nómina"},
  ];

  const filtered = empleadosVisibles.filter(e=>{
    const ms=e.nombre.toLowerCase().includes(search.toLowerCase())||(e.cedula||"").includes(search)||e.cargo.toLowerCase().includes(search.toLowerCase());
    const mf=filterStatus==="todos"||e.estado===filterStatus;
    return ms&&mf;
  });

  if (authed && mustChangePass) return (
    <>
      <style>{css}</style>
      <ChangePasswordModal user={currentUser} onSave={handleChangePassword} />
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}
    </>
  );

  if (!authed) return (
    <>
      <style>{css}</style>
      <div className="login-wrap">
        <div className="login-card">
          <div className="login-logo">
            <img src="/Logo.png" alt="Angela Duran" style={{width:"180px",margin:"0 auto 1rem",display:"block",objectFit:"contain"}} />
            <div className="login-logo-sub">Sistema de Nómina</div>
          </div>
          <div className="login-title">Acceso al sistema</div>
          {loginError&&<div className="login-error">{loginError}</div>}
          <div className="form-group"><label className="form-label">Usuario</label><input className="form-input" value={loginForm.user} onChange={e=>setLoginForm(f=>({...f,user:e.target.value}))} placeholder="Tu usuario" onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
          <div className="form-group"><label className="form-label">Contraseña</label><input className="form-input" type="password" value={loginForm.pass} onChange={e=>setLoginForm(f=>({...f,pass:e.target.value}))} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
          <button className="btn-primary" onClick={handleLogin} disabled={loginLoading}>{loginLoading?"Ingresando...":"Ingresar"}</button>

        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="ad-app">

        {/* HEADER MÓVIL */}
        <div className="mobile-header">
          <img src="/Logo.png" alt="Angela Duran" className="mobile-header-logo" />
          <div className="mobile-header-user">
            <div className="mobile-header-avatar">{initials(currentUser.nombre)}</div>
            <span style={{fontWeight:500}}>{ROLES_LABEL[currentUser.rol]}</span>
            <button className="btn-logout" style={{color:"#555"}} onClick={()=>setShowChangePassCEO(true)}>🔑</button>
            <button className="btn-logout" onClick={()=>{setAuthed(false);setCurrentUser(null);setLoginForm({user:"",pass:""});localStorage.removeItem('token');setEmployees([]);setRegistros([]);setDesprendibles({});}}>Salir</button>
          </div>
        </div>

        <div className="layout">
          <aside className="sidebar">
            <div className="sidebar-logo" style={{textAlign:"center",padding:"1.5rem 1rem"}}>
              <img src="/Logo.png" alt="Angela Duran" style={{width:"140px",objectFit:"contain",display:"block",margin:"0 auto 6px"}} />
              <div className="sidebar-logo-sub">Grupo Angela Durán SAS</div>
            </div>
            <nav className="sidebar-nav">
              <div className="nav-section-label">Menú</div>
              {navItems.map(n=>(
                <div key={n.id} className={`nav-item ${page===n.id?"active":""}`} onClick={()=>setPage(n.id)}>
                  <span className="nav-icon">{n.icon}</span><span>{n.label}</span>
                  {n.badge&&<span className="nav-badge">{n.badge}</span>}
                </div>
              ))}
            </nav>
            <div style={{padding:"0.75rem 1.5rem",borderTop:"1px solid #f0f0f0"}}>
              <div style={{fontSize:10,color:"#bbb",letterSpacing:"0.1em",textAlign:"center",lineHeight:1.8}}>
                <div style={{fontStyle:"italic",fontSize:11,color:"#ccc",marginBottom:2}}>Moda que innova</div>
                © 2026 Grupo Angela Durán SAS<br/>Todos los derechos reservados
              </div>
            </div>
            <div className="sidebar-footer">
              <div className="user-info">
                <div className="user-avatar">{initials(currentUser.nombre)}</div>
                <div><div className="user-name">{currentUser.nombre}</div><div className="user-role">{ROLES_LABEL[currentUser.rol]}</div></div>
                <div style={{display:"flex",flexDirection:"column",gap:4,marginLeft:"auto"}}>
                  <button className="btn-logout" style={{color:"#555"}} onClick={()=>setShowChangePassCEO(true)}>🔑</button>
                  <button className="btn-logout" onClick={()=>{setAuthed(false);setCurrentUser(null);setLoginForm({user:"",pass:""});localStorage.removeItem('token');setEmployees([]);setRegistros([]);setDesprendibles({});}}>Salir</button>
                </div>
              </div>
            </div>
          </aside>

          <main className="main">
            {page==="dashboard"&&(
              <>
                <div className="main-header"><div><div className="page-title">Dashboard</div><div className="page-sub">Resumen general</div></div><button className="btn-add" onClick={()=>{setEditEmp(null);setShowEmpModal(true);}}>+ Nuevo empleado</button></div>
                <div className="main-content">
                  <div className="stats-grid">
                    {[{label:"Empleados activos",value:activos,sub:`de ${empleadosVisibles.length} total`,accent:"#111"},{label:"Pendientes",value:pendientesCount,sub:"Por aprobar",accent:pendientesCount>0?"#E65100":"#2E7D52"},{label:"Registros este mes",value:registros.filter(r=>r.fecha.startsWith(new Date().toISOString().slice(0,7))).length,sub:"Total marcaciones",accent:"#1565C0"},{label:"Aprobados este mes",value:registros.filter(r=>r.estado==="aprobado"&&r.fecha.startsWith(new Date().toISOString().slice(0,7))).length,sub:"Días liquidables",accent:"#2E7D52"}].map(s=>(
                      <div key={s.label} className="stat-card" style={{"--accent":s.accent}}><div className="stat-label">{s.label}</div><div className="stat-value">{s.value}</div><div className="stat-sub">{s.sub}</div></div>
                    ))}
                  </div>
                  {pendientesCount>0&&<div className="alert-box" style={{cursor:"pointer"}} onClick={()=>setPage("horarios")}>⏳ <strong>{pendientesCount} registro{pendientesCount>1?"s":""} pendiente{pendientesCount>1?"s":""}</strong> — <u>ir a Horarios</u></div>}
                  {employees.length===0 ? (
                    <div className="empty-state"><div className="empty-icon">◉</div><div>No hay empleados aún</div><button className="btn-add" style={{margin:"1rem auto 0",display:"inline-flex"}} onClick={()=>{setEditEmp(null);setShowEmpModal(true);}}>+ Crear primer empleado</button></div>
                  ) : (
                    <table className="data-table">
                      <thead><tr><th>Empleado</th><th>Cargo</th><th>Salario</th><th>Estado</th></tr></thead>
                      <tbody>{employees.slice(0,6).map(e=>(
                        <tr key={e.id} style={{cursor:"pointer"}} onClick={()=>{setDetailEmp(e);setPage("empleados");}}>
                          <td><div className="emp-name-cell"><div className="emp-avatar">{initials(e.nombre)}</div><div><div className="emp-name">{e.nombre}</div><div className="emp-id">CC {e.cedula}</div></div></div></td>
                          <td style={{color:"#555"}}>{e.cargo}</td><td>{fmt(e.salario)}</td>
                          <td><span className={`badge badge-${e.estado}`}>{e.estado==="activo"?"Activo":"Inactivo"}</span></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </div>
              </>
            )}
            {page==="empleados"&&(
              <>
                <div className="main-header">
                  <div><div className="page-title">{detailEmp?detailEmp.nombre:"Empleados"}</div><div className="page-sub">{detailEmp?detailEmp.cargo:`${activos} activos`}</div></div>
                  {!detailEmp&&<button className="btn-add" onClick={()=>{setEditEmp(null);setShowEmpModal(true);}}>+ Nuevo empleado</button>}
                </div>
                <div className="main-content">
                  {detailEmp ? <EmployeeDetail emp={detailEmp} onBack={()=>setDetailEmp(null)} onEdit={e=>{setEditEmp(e);setShowEmpModal(true);setDetailEmp(null);}} onReset={e=>{setResetEmp(e);setDetailEmp(null);}} desprendibles={desprendibles} setDesprendibles={setDesprendibles} showToast={showToast} refreshDesp={loadData} /> : (
                    <>
                      <div className="search-bar">
                        <input className="search-input" placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
                        <select className="form-select" style={{width:"auto",minWidth:140}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}><option value="todos">Todos</option><option value="activo">Activos</option><option value="inactivo">Inactivos</option></select>
                      </div>
                      <table className="data-table">
                        <thead><tr><th>Empleado</th><th>Cargo</th><th>Salario</th><th>Rol</th><th>Estado</th><th></th></tr></thead>
                        <tbody>
                          {filtered.length===0?<tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">◎</div>Sin resultados</div></td></tr>
                          :filtered.map(e=>(
                            <tr key={e.id}>
                              <td style={{cursor:"pointer"}} onClick={()=>setDetailEmp(e)}><div className="emp-name-cell"><div className="emp-avatar">{initials(e.nombre)}</div><div><div className="emp-name">{e.nombre}</div><div className="emp-id">CC {e.cedula}</div></div></div></td>
                              <td style={{color:"#555"}}>{e.cargo}</td><td>{fmt(e.salario)}</td>
                              <td><span className={`badge badge-${e.rol}`}>{ROLES_LABEL[e.rol]}</span></td>
                              <td><span className={`badge badge-${e.estado}`}>{e.estado==="activo"?"Activo":"Inactivo"}</span></td>
                              <td><div className="action-btns">
                                <button className="btn-icon" onClick={()=>setDetailEmp(e)}>👁</button>
                                <button className="btn-icon" onClick={()=>{setEditEmp(e);setShowEmpModal(true);}}>✏</button>
                                {confirmDeleteEmp===e.id ? (
                  <div style={{display:"flex",gap:4,alignItems:"center"}}>
                    <span style={{fontSize:11,color:"#D32F2F"}}>¿Seguro?</span>
                    <button className="btn-icon danger" onClick={()=>{setEmployees(es=>es.filter(x=>x.id!==e.id));setConfirmDeleteEmp(null);showToast("Empleado eliminado","error");}}>✓</button>
                    <button className="btn-icon" onClick={()=>setConfirmDeleteEmp(null)}>✕</button>
                  </div>
                ) : (
                  <button className="btn-icon danger" onClick={()=>setConfirmDeleteEmp(e.id)}>✕</button>
                )}
                              </div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </>
            )}
            {page==="horarios"&&(
              <>
                <div className="main-header"><div><div className="page-title">Horarios</div><div className="page-sub">Registro y aprobación</div></div></div>
                <div className="main-content"><HorariosPage registros={registros} setRegistros={setRegistros} employees={employees} isCEO={isCEO} showToast={showToast} refreshData={loadData} /></div>
              </>
            )}
            {page==="nomina"&&(
              <>
                <div className="main-header"><div><div className="page-title">Nómina</div><div className="page-sub">Liquidación automática mensual</div></div></div>
                <div className="main-content"><NominaPage employees={employees} registros={registros} showToast={showToast} desprendibles={desprendibles} setDesprendibles={setDesprendibles} /></div>
              </>
            )}
            {page==="mi-horario"&&(
              <>
                <div className="main-header"><div><div className="page-title">Mis horarios</div><div className="page-sub">Registra tu jornada diaria</div></div></div>
                <div className="main-content"><MiHorarioPage empId={currentUser.empId} registros={registros} setRegistros={setRegistros} showToast={showToast} refreshData={loadData} /></div>
              </>
            )}
            {page==="mis-documentos"&&(
              <>
                <div className="main-header"><div><div className="page-title">Mis documentos</div><div className="page-sub">Desprendibles, certificados y afiliaciones</div></div></div>
                <div className="main-content"><MisDocumentosPage empId={currentUser.empId} desprendibles={desprendibles} /></div>
              </>
            )}
            {page==="mi-perfil"&&(
              <>
                <div className="main-header"><div><div className="page-title">Mi perfil</div><div className="page-sub">Tu información personal</div></div></div>
                <div className="main-content"><MiPerfilPage currentUser={currentUser} employees={employees} onUpdate={handleSaveEmployee} showToast={showToast} /></div>
              </>
            )}
          </main>
        </div>
      </div>
      {showChangePassCEO&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowChangePassCEO(false)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header"><h2 className="modal-title">Cambiar mi contraseña</h2><button className="modal-close" onClick={()=>setShowChangePassCEO(false)}>✕</button></div>
            <div className="modal-body">
              <ChangePassForm onSave={async(newPass)=>{ try { await apiFetch('/change-password',{method:'POST',body:JSON.stringify({newPassword:newPass})}); setShowChangePassCEO(false); showToast("Contraseña actualizada ✓"); } catch(e){ showToast("Error: "+e.message,"error"); } }} onClose={()=>setShowChangePassCEO(false)} />
            </div>
          </div>
        </div>
      )}
      {showEmpModal&&<EmployeeModal emp={editEmp} onSave={handleSaveEmployee} onClose={()=>{setShowEmpModal(false);setEditEmp(null);}} />}
      {resetEmp&&<ResetPasswordModal emp={resetEmp} onSave={(p)=>handleReset(resetEmp,p)} onClose={()=>setResetEmp(null)} />}
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}

      {/* BOTTOM NAV MÓVIL */}
      <nav className="bottom-nav">
        {navItems.map(n=>(
          <button key={n.id} className={`bottom-nav-item ${page===n.id?"active":""}`} onClick={()=>!n.soon&&setPage(n.id)}>
            {n.badge&&<span className="bottom-nav-badge">{n.badge}</span>}
            <span className="bottom-nav-icon">{n.id==="dashboard"?"⊞":n.id==="empleados"?"◉":n.id==="horarios"?"◷":n.id==="nomina"?"◎":n.id==="mi-horario"?"◷":"◎"}</span>
            <span>{n.label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}
